import React, { useState, useEffect, useMemo, useRef, useId } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  SHEET_HEADERS,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { FaRegClock, FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import { getAuth } from "firebase/auth";
import PreTradeChecklistModal from './PreTradeChecklistModal';
import { savePreTradeChecklist, getTodayPreTradeChecklist,setChecklistShown } from "../services/FirestoreService";
import { createPortal } from "react-dom";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase";
import SimpleCalculator from './SimpleCalculator'; 
import { checkFirstTradeAnalysis } from "../services/FirstTradeAnalysisService"; // Logic
import FirstTradeAnalysisPopup from "../components/FirstTradeAnalysisPopup";     // UI
import { FUTURES_PRODUCTS, FOREX_PAIRS, FOREX_INDICES, isIndexSymbol } from "../constants/instruments";

// --- Icons (copy these SVGs exactly as shown) ---
const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/>
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

const IconJournal = () => (
  <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <rect x="4" y="6" width="16" height="14" rx="2" stroke="#f59e42" strokeWidth="2"/>
      <path d="M8 2v4M16 2v4" stroke="#f59e42" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

// --- Constants for dropdowns ---
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Sydney","Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell", "CounterTrend - Buy","CounterTrend - Sell", "Did Not Take"];

const PRODUCT_OPTIONS = FUTURES_PRODUCTS;

// --- Default Settings ---
const defaultSettings = {
  "Theme": "Dark", "Pretrade Checklist": "Yes", "Calculator Only": false,
  "Daily Goal": 500, "Daily Loss Limit": 200, "Max Trades Per Day": 10,
  "Max Loss Trades Per Day": 5, "Drawdown Limit %": 20, "Max Lot Size": 10,
  "Min Lot Size": 0.01, "Account Currency": "USD", "Leverage": 100,
  "Calculation by Entry Price": false, "Max Trades Per Day Toggle": false,
  "Max Loss Trades Per Day Toggle": false, "Drawdown Limit % Toggle": true, "Maximum Contracts": 10,
  "Minimum Contracts":1, 
  "Risk Mode": "Percent",         // "Percent" | "Dollar"
  "Risk Percentage": 3.00,        // used when Risk Mode = Percent
  "Risk Amount": 0,               // used when Risk Mode = Dollar
  "Stop Loss": 25, "Take Profit": 50,
  "Product": "", "Initial Balance": 0, "Account Goal": 0, "Running Balance": 0,
  "Number of Trades to Goal": 0,
  "OvertradingPopupShownDate": "", "DrawdownPopupShownDate": "", "TooManyLossesPopupShownDate": "",
  "DailyGoalPopupShownDate": "", "DailyGoalPopupShownForGoal":"","LossLimitWarningShownDate": "",
  "MoodVolatilityPopupShownDate": "", "OverRiskWarningShownDate": "", "ConsecutiveWinsPopupShownDate": "",
  "AccountGoalReachedShownDate": "", "LessonsLearnedStreakWarningShownDate": "","RiskOfRuinWarningShownDate": "",
  "TwoLossWarningShownDate": "", "ThreeLossWarningShownDate": "", "MaxLossLimitBlownShownDate": "",
  "TwoLossAfterGoalWarningShownDate": "", "Win10NoRulesPopupShownDate": "", "Win10WithRulesPopupShownDate": "",
  "PretradeChecklistShownDate": "",
  "DrawdownLockActive": false,
  "DrawdownLockStartedDate": "",
  "DrawdownLockPeak": 0,
};


const defaultJournal = {
  "Product": null,
  "Actual Balance": "",
  "Position": "",
  "Date": "",
  "Session": "",
  "Entry Time": "",
  "Exit Time": "",
  "Entry Price": "",
  "Exit Price": "",
  "Entry Strategy": "",
  "Exit Strategy": "",
  "Did I follow my Rules?": "",
  "Size": 0,
  "Ticks":"",
  "Suggested Max # of Contracts": 0, // Futures only
  "Win": false,
  "Loss": false,
  "Mood_Mindset": "",
  "IdealSL":"",
  "Grade": "",
  "Notes": "",
  "Emotional Notes": "",
  "Links_Screenshots": "",
  "Lesson": "",
  "LogLesson": false,
  "Potential": "",
  "Timeframe": "",
  "Needs Review": false,
  "Target Reached": false
};

// --- Timeframe options for the Journal combobox ---
const TIMEFRAME_OPTIONS = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];


// --- Helper functions (keep at top) ---
function getPricePerTick(productStr) {
  if (!productStr) return 0;
  const match = productStr.match(/\$\s?([0-9,.]+)/);
  return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
}

function getSessionFromTime(time24) {
  if (!time24) return "";
  
  const [hour] = time24.split(":").map(Number);

  // New York: 8:00 AM - 5:00 PM (17:00)
  if (hour >= 8 && hour < 17) return "New York";
  
  // London: 3:00 AM - 8:00 AM
  if (hour >= 3 && hour < 8) return "London";
  
  // Asian (Tokyo): 7:00 PM (19:00) - 3:00 AM 
  // (Using || because it crosses midnight)
  if (hour >= 19 || hour < 3) return "Asian";
  
  // Sydney: 5:00 PM (17:00) - 7:00 PM (19:00)
  // (Captures the gap after NY closes and before Tokyo opens)
  if (hour >= 17 && hour < 19) return "Sydney";
  
  return "";
}


// NEW: get product symbol and tick size
function getProductSymbol(productStr = "") {
  return String(productStr).split("-")[0].trim().toUpperCase();
}

const TICK_SIZES = {
  YM: 1,
  MYM: 1,
  ES: 0.25,
  MES: 0.25,
  NQ: 0.25,
  MNQ: 0.25,
  RT: 0.1,
  M2K: 0.1,
  GC: 0.1,
  MGC: 0.1,
  CL: 0.01,
  MCL: 0.01,
  NG: 0.001,
  MNG: 0.001,
  SI: 0.005,
  SIL: 0.005,
  // fallback for anything not listed
};

function getTickSize(productStr = "") {
  const sym = getProductSymbol(productStr);
  return TICK_SIZES[sym] || 1;  // default 1 tick per price unit (works for YM)
}

function getTickSizeForProduct(productStr) {
  if (!productStr) return null;

  const code = String(productStr).split("-")[0].trim().toUpperCase();

  // Tick sizes in PRICE units per tick.
  // Add/adjust here for any other markets you trade.
  const TABLE = {
    // Index futures
    ES: 0.25,
    MES: 0.25,
    NQ: 0.25,
    MNQ: 0.25,
    YM: 1,
    MYM: 1,
    RT: 0.1,    // Russell 2000 (RTY)
    RTY: 0.1,
    M2K: 0.1,

    // Metals
    GC: 0.1,
    MGC: 0.1,
    SI: 0.005,
    SIL: 0.005,

    // Energies
    CL: 0.01,
    MCL: 0.01,
    NG: 0.001,
    MNG: 0.001,

    // Grains
    ZS: 0.25,
    ZC: 0.25,
    ZW: 0.25,
    MZS: 0.25,
    MZC: 0.25,
    MZW: 0.25,

    // Crypto (CME)
    BTC: 5,
    MBT: 5,
  };

  return TABLE[code] ?? null;
}

function computeTicksFromPricesAbs(productStr, entryPrice, exitPrice) {
  // ✅ Only calculate if BOTH prices are actually provided
  if (entryPrice == null || exitPrice == null) return null;

  const entryStr = String(entryPrice).trim();
  const exitStr  = String(exitPrice).trim();

  if (entryStr === "" || exitStr === "") return null;

  const tickSize = getTickSizeForProduct(productStr);
  if (!tickSize || tickSize <= 0) return null;

  const entry = Number(entryStr);
  const exit  = Number(exitStr);
  if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;

  const diff = Math.abs(exit - entry);
  if (!diff) return 0;

  const rawTicks = diff / tickSize;
  if (!Number.isFinite(rawTicks)) return null;

  // Round to nearest whole tick
  return Math.round(rawTicks);
}


function formatCurrency(val) {
  if (val === "" || val === null || typeof val === "undefined" || isNaN(val)) return "";
  return Number(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>
        {value}
      </span>
    </div>
  );
}

function useBlockWheelOnNumber(inputRef) {
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const stopWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", stopWheel, { passive: false });
    return () => el.removeEventListener("wheel", stopWheel);
  }, [inputRef]);
}

// Treat these keys as "clearable numeric" (keep raw string while typing)
const RISK_KEYS = new Set(["Risk Percentage", "Risk Dollar", "Risk Amount", "Risk Dollar Amount"]);

// Helpers for blur-commit
const isDec = (s) => s === "" || /^\d*\.?\d*$/.test(s);
const commitNumberOrEmpty  = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) ? n : "";
};
// For $ risk: must be > 0, but no upper limit
const commitPositiveOrEmpty = (s) => {
  const t = String(s).trim();
  if (t === "") return "";
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : "";
};


function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str; // already 24h
  const m = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (m) {
    let [, h, mins, ap] = m;               // <-- note the leading comma to skip the full match
    let hour = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (ap.toUpperCase() === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mins}`;
  }
  return "";
}

function toAmPm(str) {
  // Convert "13:15" to "01:15 PM"
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}

// Converts "01:23 PM" or "13:23" to { h, m, s }
function parseTimeToParts(str) {
  if (!str) return null;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return { h, m, s: s || 0 };
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [, h, m, ap] = match;              // <-- fixed
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return { h, m, s: 0 };
  }
  return null;
}

// Duration in HH:MM:SS (handles overnight crossing)
function getDurationHHMMSS(start, end) {
  const et = parseTimeToParts(start);
  const xt = parseTimeToParts(end);
  if (!et || !xt) return "";
  let startSec = et.h * 3600 + et.m * 60 + (et.s || 0);
  let endSec = xt.h * 3600 + xt.m * 60 + (xt.s || 0);
  let diff = endSec - startSec;
  if (diff < 0) diff += 24 * 3600; // Crosses midnight
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// "01:23 PM" | "13:23" | "13:23:00" -> total seconds
function timeStringToSeconds(str) {
  if (!str) return 0;
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(str)) {
    const [h, m, s] = str.split(":").map(Number);
    return (h * 3600) + (m * 60) + (s || 0);
  }
  const match = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (match) {
    let [, h, m, ap] = match;              // <-- fixed
    h = parseInt(h, 10);
    m = parseInt(m, 10);
    if (ap.toUpperCase() === "PM" && h !== 12) h += 12;
    if (ap.toUpperCase() === "AM" && h === 12) h = 0;
    return (h * 3600) + (m * 60);
  }
  return 0;
}

// Converts seconds to "HH:MM:SS"
function secondsToHHMMSS(totalSeconds) {
  totalSeconds = Math.max(0, totalSeconds);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0"),
  ].join(":");
}

function decimalToHHMMSS(decimal) {
  if (!decimal || isNaN(decimal)) return "00:00:00";
  const totalSeconds = Math.round(Number(decimal) * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0")
  ].join(":");
}

// Convert "HH:MM:SS" → "h:mm" (drop seconds, don't left‑pad hours)
function hhmmssToHMM(hhmmss) {
  if (!hhmmss) return "";
  const parts = String(hhmmss).split(":");
  if (parts.length < 2) return "";
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return `${h}:${m.toString().padStart(2, "0")}`;
}


function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}

function clampPct(n) {
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

// Add this once near the top of CalculateForm.jsx
function getTodayISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}



// --- Main Component ---
export default function CalculateForm({ userId, accountId }) {
  const [settings, setSettings] = useState({ ...defaultSettings });
  const [journal, setJournal] = useState(() => ({
  ...defaultJournal,
  Date: getTodayLocalISO(),
}));

  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTrade, setSavingTrade] = useState(false);
  const [calculatedSummary, setCalculatedSummary] = useState(null);

  // Modal/Popup states
  const [missingFields, setMissingFields] = useState([]);
  const [showZeroContractsPopup, setShowZeroContractsPopup] = useState(false);
  const [lossForDay, setLossForDay] = useState(0);
  const [lossLimitAmount, setLossLimitAmount] = useState(0);
  //const [overRiskMessage, setOverRiskMessage] = useState("");
  //const [warningFlags, setWarningFlags] = useState({}); // This can probably be removed now
  const [projectionOpen, setProjectionOpen] = useState(false); // For Win Projection Table
  const [lossProjectionOpen, setLossProjectionOpen] = useState(false); // For Loss Projection Table
  const [showDaySeparators, setShowDaySeparators] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [lastRiskPercent, setLastRiskPercent] = useState(0);
  const [lastPercentOfBalance, setLastPercentOfBalance] = useState(0);
  const [lastTotalRisk, setLastTotalRisk] = useState(0);
  const [lastActualRiskPercent, setLastActualRiskPercent] = useState(0);
  const drawdownLimitPct = Number(settings["Drawdown Limit %"] || 20);
  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const [userEditingSize, setUserEditingSize] = useState(false);
  const [manualCalculate, setManualCalculate] = useState(false);
  const [autoCalculatedOnce, setAutoCalculatedOnce] = useState(false);
  const [drawdownAmount, setDrawdownAmount] = useState(0);
  const [drawdownPct, setDrawdownPct] = useState(0);
  const [dailyGoalProg, setDailyGoalProg] = useState({ done: 0, target: 0, pct: 0 });
  const [dailyLossProg, setDailyLossProg] = useState({ lost: 0, limit: 0, pct: 0 });
  const [entryStrategies, setEntryStrategies] = useState([]);
  const [exitStrategies, setExitStrategies] = useState([]);
  const [showPTC, setShowPTC] = useState(false);
  const [ptcPrefill, setPTCPrefill] = useState({});
  const actualBalRef = useRef(null);
  const [showCalculator, setShowCalculator] = useState(false);



  // Popup Queue States
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null); // { id: 'popupName', data: { message: '...', ... } }

  // Projections (for win/loss tables)
  const [winProjection, setWinProjection] = useState({ trades: [], tradeCount: 0 });
  const [lossProjection, setLossProjection] = useState({ trades: [], tradeCount: 0, suggestedContracts: 0 });
  const { width, height } = useWindowSize();
  const [showChecklist, setShowChecklist] = useState(false);
  const [userPrefs, setUserPrefs] = useState(() => {
    // Initialize synchronously from localStorage to prevent balance flashing
    const cached = localStorage.getItem("isPrivacyMode");
    return cached === "true" ? { "Privacy Mode": true } : {};
  });
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const [initialSettings, setInitialSettings] = useState(null);
  const popupsAllowedRef = useRef(false);
  const DEFAULT_TIME_ZONE = undefined;
  const [subStatus, setSubStatus] = useState("");

  useEffect(() => {
    if (!userId) return;
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSubStatus(snapshot.docs[0].data().status || "");
      } else {
        setSubStatus("");
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const isTrialing = subStatus === "TRIALING";


async function checkDailyLossLimit(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  try {
    const todayISO = getTodayLocalISO();

    const lossLimit = Number(currentSettings["Daily Loss Limit"] || 0);
    if (lossLimit <= 0) return;

    // --- Robust date check (same approach as refreshDailyBars) ---
    const isTodayRow = (d) => {
      if (!d) return false;
      if (typeof d === "string") {
        if (d.includes("-")) return d.startsWith(todayISO); // "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm"
        if (d.includes("/")) {                               // "MM/DD/YYYY"
          const [mm, dd, yyyy] = d.split("/");
          const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
          return iso === todayISO;
        }
        const dt = new Date(d);
        return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
      }
      if (d?.seconds) {
        return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
      }
      try {
        return new Date(d).toISOString().slice(0,10) === todayISO;
      } catch {
        return false;
      }
    };
    // -------------------------------------------------------------

    const todays = (trades || []).filter(r => isTodayRow(r["Date"]));
    const net = todays.reduce((sum, r) => sum + Number(r["Actual Reward"] || 0), 0);
    const netLossAbs = Math.max(0, -net);

    const shownDateKey = "LossLimitWarningShownDate";

    if (netLossAbs >= lossLimit && currentSettings[shownDateKey] !== todayISO) {
      queuePopup("lossLimitWarning", {
        lossForDay: netLossAbs,
        lossLimitAmount: lossLimit,
      });

      const newSettings = { ...currentSettings, [shownDateKey]: todayISO };
      await updateSettingsFunc(userId, accountId, newSettings);
      setLocalSettings(newSettings);
    } else if (netLossAbs < lossLimit && currentSettings[shownDateKey] === todayISO) {
      const newSettings = { ...currentSettings, [shownDateKey]: "" };
      await updateSettingsFunc(userId, accountId, newSettings);
      setLocalSettings(newSettings);
    }
  } catch (e) {
    console.error("checkDailyLossLimit error:", e);
  }
}

const [userFavorites, setUserFavorites] = useState({ futures: [], forex: [] });

useEffect(() => {
  if (!userId) return;

  const ref = doc(db, "users", userId, "settings", "userSettings");
  const unsub = onSnapshot(ref, snap => {
    const favs = snap.exists() ? (snap.data().favorites || { futures: [], forex: [] }) 
                               : { futures: [], forex: [] };
    setUserFavorites(favs);
  });

  return unsub; // cleanup
}, [userId]);

useEffect(() => {
  setJournal(prev => {
    const next = { ...prev };
    let changed = false;

    // Helper to apply default if field is empty
    const applyDefault = (field, settingKey) => {
      const defVal = String(settings[settingKey] || "").trim();
      if (defVal && !String(next[field] || "").trim()) {
        next[field] = defVal;
        changed = true;
      }
    };

    applyDefault("Entry Strategy", "Default Entry Strategy");
    applyDefault("Exit Strategy", "Default Exit Strategy");
    applyDefault("Timeframe", "Default Timeframe");
    applyDefault("Session", "Default Session"); // ✅ ADDED THIS LINE

    return changed ? next : prev;
  });
}, [
  settings["Default Entry Strategy"], 
  settings["Default Exit Strategy"], 
  settings["Default Timeframe"],
  settings["Default Session"] // ✅ ADDED THIS DEPENDENCY
]);

const favoriteFuturesOptions = useMemo(() => {
  const favCodes = (userFavorites?.futures || []).map(s => String(s).toUpperCase());
  if (!favCodes.length) return [];
  return favCodes
    .map(code => PRODUCT_OPTIONS.find(([v]) => String(v).startsWith(code)))
    .filter(Boolean);
}, [userFavorites, PRODUCT_OPTIONS]);

const favoriteValues = useMemo(
  () => new Set(favoriteFuturesOptions.map(([v]) => v)),
  [favoriteFuturesOptions]
);

const nonFavoriteOptions = useMemo(
  () => PRODUCT_OPTIONS.filter(([v]) => !favoriteValues.has(v)),
  [favoriteValues]
);

useEffect(() => {
  setJournal(prev => {
    const cur = String(prev["Timeframe"] || "").trim();
    if (cur) return prev;

    const defTf = String(settings?.["Default Timeframe"] || "").trim();
    return defTf ? { ...prev, "Timeframe": defTf } : prev;
  });
}, [settings?.["Default Timeframe"]]);



function getDailyGoalMessage(timeZone = DEFAULT_TIME_ZONE) {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    ...(timeZone ? { timeZone } : {})
  }).format(now);

  if (weekday === 'Sunday' || weekday === 'Monday') {
    return "You are off to a great start of the week. Keep the momentum, consider stopping for the day and enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Tuesday' || weekday === 'Wednesday') {
    return "You have the rest of the week!! The market will always be there, but this moment in time will be gone forever. Don't forget to enjoy the things that matter outside of trading!";
  }
  if (weekday === 'Thursday') {
    return "Go and enjoy the rest of your day!";
  }
  if (weekday === 'Friday') {
    return "Today is Capital Preservation Friday! Consider stopping for the day and get ready for a great weekend!!";
  }
  // Saturday or anything unexpected — fallback
  return "Go and enjoy the rest of your day!";
}

const refreshDailyBars = async (s = settings) => {
  try {
    const allRows = await fetchJournalRows(userId, accountId);
    const todayISO = getTodayLocalISO();

    // Robust “is this today?” — handles "YYYY-MM-DD", "MM/DD/YYYY", and Firestore Timestamp
    const isTodayRow = (d) => {
      if (!d) return false;
      if (typeof d === "string") {
        if (d.includes("-")) return d.startsWith(todayISO); // "YYYY-MM-DD"
        if (d.includes("/")) {                               // "MM/DD/YYYY"
          const [mm, dd, yyyy] = d.split("/");
          const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
          return iso === todayISO;
        }
        const dt = new Date(d);
        return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
      }
      if (d?.seconds) {
        return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
      }
      try {
        return new Date(d).toISOString().slice(0,10) === todayISO;
      } catch {
        return false;
      }
    };

    const todayRows = allRows.filter(r => isTodayRow(r["Date"]));
    const todayPnL  = todayRows.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);

    const dailyGoal      = Number(s["Daily Goal"] || 0);
    const dailyLossLimit = Number(s["Daily Loss Limit"] || 0);

    // amounts
    const positivePnL = Math.max(0, todayPnL); // towards goal
    const lossAbs     = Math.max(0, -todayPnL);// loss toward loss-limit

    // percents
    const goalPct = dailyGoal > 0 ? clampPct((positivePnL / dailyGoal) * 100) : 0;
    const lossPct = dailyLossLimit > 0 ? clampPct((lossAbs / dailyLossLimit) * 100) : 0;

    setDailyGoalProg({ done: positivePnL, target: dailyGoal,      pct: goalPct });
    setDailyLossProg({ lost: lossAbs,     limit: dailyLossLimit,  pct: lossPct });
  } catch (e) {
    setDailyGoalProg({ done: 0, target: Number(settings["Daily Goal"] || 0), pct: 0 });
    setDailyLossProg({ lost: 0, limit: Number(settings["Daily Loss Limit"] || 0), pct: 0 });
  }
};

  // Function to get today's date in ISO format (YYYY-MM-DD)
function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}

function clampPct(n) {
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}


// Place this helper function inside your CalculateForm component, before the return statement.

function highlightHundredPercent(line) {
  if (!line) return null;

  // This splits the line by "100%" but keeps "100%" in the array.
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">
        100%
      </span>
    ) : (
      // Use a React.Fragment to avoid adding extra wrapper elements
      <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}

// Function to add a popup to the queue (with priority: twoLossAfterGoalWarning > twoLossWarning)
const queuePopup = (id, data = {}) => {

  if (!popupsAllowedRef.current) {
    return;
  }
  setPopupQueue(prev => {
    let next = prev;

    if (id === 'maxLossLimitBlown') {
      // Drop any already-queued riskOfRuinWarning
      next = next.filter(p => p.id !== 'riskOfRuinWarning');
    }
    if (id === 'riskOfRuinWarning') {
      const blownPending = next.some(p => p.id === 'maxLossLimitBlown');
      const blownShowing = currentPopup?.id === 'maxLossLimitBlown';
      if (blownPending || blownShowing) return next; // suppress ROR
    }

    // If the high-priority "twoLossAfterGoalWarning" is being queued,
    // drop any already-queued generic two-loss warning.
    if (id === 'twoLossAfterGoalWarning') {
      next = next.filter(p => p.id !== 'twoLossWarning');
    }

    // If trying to queue the generic two-loss warning but the after-goal warning
    // is already queued or currently showing, suppress the generic one.
    if (id === 'twoLossWarning') {
      const higherPending = next.some(p => p.id === 'twoLossAfterGoalWarning');
      const higherShowing = currentPopup?.id === 'twoLossAfterGoalWarning';
      if (higherPending || higherShowing) return next;
    }

    // Normal de-duplication rules

        if (next.some(p => p.id === id) || currentPopup?.id === id) {
          return next;
        }
        const out = [...next, { id, data }];
        return out;
  });
};



  // Function to close the current popup and show the next
  const closeCurrentPopup = () => {
    setCurrentPopup(null); // This will trigger the useEffect below to show the next popup
  };

  useEffect(() => {
  const load = async () => {
    try {
      const settingsObj = await getSettings(userId, accountId);
            setSettings(prev => ({ ...prev, ...(settingsObj || {}) }));
            // (optional) if you use initialSettings elsewhere for auto-calc:
            setInitialSettings(settingsObj || {});
      if (initialSettings === null) {
        setInitialSettings(settingsObj); // <- Capture the very first load
      }
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };
  load();
  // Only run this when userId/accountId change or initialSettings is not set
  // eslint-disable-next-line
}, [userId, accountId]);

  useEffect(() => {
    if (!userId || !accountId) return;
    // Refresh when these settings change, so the amounts update immediately
    refreshDailyBars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, accountId, settings["Daily Goal"], settings["Daily Loss Limit"]]);



useEffect(() => {
    if (!userId) return;
    getUserSettings(userId).then(data => {
      // If the prefs doc doesn't exist (data = {}), set default prefs!
      if (!data || Object.keys(data).length === 0) {
        // Pick your defaults here
        const defaultPrefs = {
          "Pretrade Checklist": "Yes",   // Or "No" if you want off by default
          "PretradeChecklistShownDate": ""
          // ...add any other default user prefs you want here
        };
        setUserPrefs(defaultPrefs);
        // ✅ Cache default to local storage
        localStorage.setItem("isPrivacyMode", "false"); 
        updateUserSettings(userId, defaultPrefs); // Save to Firestore
      } else {
        setUserPrefs(data);
        // ✅ Cache fetched setting to local storage
        localStorage.setItem("isPrivacyMode", data["Privacy Mode"] === true ? "true" : "false"); 
      }
    });
  }, [userId]);


  // Effect to manage the popup queue
// Effect to manage the popup queue
useEffect(() => {
    if (currentPopup === null && popupQueue.length > 0) {
      const nextPopup = popupQueue[0];
      setPopupQueue(popupQueue.slice(1)); // Remove the first item
      setCurrentPopup(nextPopup); // Set as current


      // Note: The timer for the 'tradeSaved' popup will be handled in handleSaveTrade now.
    }
  }, [popupQueue, currentPopup]);



async function checkConsecutiveWins(trades, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
  const todayISO = getTodayLocalISO();
  const shownKey = "ConsecutiveWinsPopupShownDate";
  const popupId  = "consecutiveWinsPopup";
  if (currentSettings?.[shownKey] === todayISO) return;

  const isWin = (v) =>
    v === true || v === 1 || /^true|yes|won$/i.test(String(v));

  const isTodayRow = (d) => {
    if (!d) return false;
    if (typeof d === "string") {
      if (d.includes("-")) return d.startsWith(todayISO);
      if (d.includes("/")) {
        const [mm, dd, yyyy] = d.split("/");
        return `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}` === todayISO;
      }
      const dt = new Date(d);
      return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
    }
    if (d?.seconds) return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
    try { return new Date(d).toISOString().slice(0,10) === todayISO; } catch { return false; }
  };

  const rowTimestamp = (row) => {
    const d = row?.["Date"];
    let base;
    if (!d) base = new Date(0);
    else if (typeof d === "string") {
      if (d.includes("-")) base = new Date(d);
      else if (d.includes("/")) {
        const [mm, dd, yyyy] = d.split("/").map(Number);
        base = new Date(yyyy || 1970, (mm || 1)-1, dd || 1);
      } else base = new Date(d);
    } else if (d?.seconds) base = new Date(d.seconds * 1000);
    else base = new Date(d);

    const t = row?.["Exit Time"] || row?.["Entry Time"] || "";
    const m = t && t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([APap][Mm])?$/);
    let secs = 0;
    if (m) {
      let hh = Number(m[1]), mm = Number(m[2]), ss = Number(m[3] || 0);
      const ap = m[4];
      if (ap) {
        if (/pm/i.test(ap) && hh !== 12) hh += 12;
        if (/am/i.test(ap) && hh === 12) hh = 0;
      }
      secs = hh * 3600 + mm * 60 + ss;
    }
    return base.getTime() + secs * 1000;
  };

  const todayTrades = (trades || []).filter(t => isTodayRow(t["Date"]));
  if (todayTrades.length < 5) return;

  // sort deterministically
  todayTrades.sort((a, b) => {
    const na = Number(a?.tradeNum ?? a?.["Trade #"]);
    const nb = Number(b?.tradeNum ?? b?.["Trade #"]);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return rowTimestamp(a) - rowTimestamp(b);
  });

  // count backward from the most recent trade
  let streak = 0;
  for (let i = todayTrades.length - 1; i >= 0; i--) {
    if (isWin(todayTrades[i]["Win"])) streak++;
    else break;
  }

  if (streak >= 5) {
    queuePopup(popupId);
    await updateSettingsFunc(userId, accountId, { [shownKey]: todayISO });
    setLocalSettings?.(prev => ({ ...prev, [shownKey]: todayISO }));
  }
}





async function checkOvertrading(trades, maxTradesPerDay, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
  const today = getTodayLocalISO();
  const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
  const popupId = "overtradingPopup";
  const shownDateKey = "OvertradingPopupShownDate";

  if (todayTrades.length > maxTradesPerDay && currentSettings[shownDateKey] !== today) {
    queuePopup(popupId, { maxTradesPerDay });
    const newSettings = { ...currentSettings, [shownDateKey]: today };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (todayTrades.length <= maxTradesPerDay && currentSettings[shownDateKey] === today) {
    // Reset if under limit
    const newSettings = { ...currentSettings, [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


async function checkTooManyLosses(
  trades,
  maxLossesPerDay,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const today = getTodayLocalISO();

  // Be tolerant of boolean/strings in the "Loss" field
  const isLoss = (v) => String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes";

  const todayLosses = trades.filter(
    (t) => (t["Date"] || "").startsWith(today) && isLoss(t["Loss"])
  );

  const popupId = "tooManyLossesPopup";
  const shownDateKey = "TooManyLossesPopupShownDate";

  // Fire when the limit is REACHED (>=), not only when it's exceeded
  if (todayLosses.length >= maxLossesPerDay && currentSettings[shownDateKey] !== today) {
    queuePopup(popupId, { maxLossesPerDay });
    const newSettings = { ...currentSettings, [shownDateKey]: today };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
  // Reset once you're back UNDER the limit
  else if (todayLosses.length < maxLossesPerDay && currentSettings[shownDateKey] === today) {
    const newSettings = { ...currentSettings, [shownDateKey]: "" };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}




async function checkMoodVolatility(
  trades,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();
  const shownDateKey = "MoodVolatilityPopupShownDate";

  // Defensive: if the flag was corrupted to an array, reset it
  if (Array.isArray(currentSettings?.[shownDateKey])) {
    await updateSettingsFunc(userId, accountId, { [shownDateKey]: "" });
    setLocalSettings?.(prev => ({ ...prev, [shownDateKey]: "" }));
  }

  if (!Array.isArray(trades)) return;

  // Only look at today's trades
  const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(todayISO));
  if (todayTrades.length < 3) return;

  const last3 = todayTrades.slice(-3);
  const moods = last3.map(t => {
    const v = Number(t["Mood_Mindset"] ?? t["Mood"] ?? t["Mood / Mindset"]);
    return Number.isFinite(v) ? v : null;
  });

  // Trigger when all three are 4 or 5
  const allHigh = moods.length === 3 && moods.every(v => v !== null && (v === 4 || v === 5));
  if (!allHigh) return;

  // Fire once per day
  if (currentSettings?.[shownDateKey] !== todayISO) {
    queuePopup("moodVolatilityPopup");
    const patch = { [shownDateKey]: todayISO };   // <-- store a DATE STRING
    await updateSettingsFunc(userId, accountId, patch);
    setLocalSettings?.(prev => ({ ...prev, ...patch }));
  }
}


// === DEBUG HELPERS ===
const DEBUG_POPUPS = false; // flip to false to silence logs

const dgroup = (label, fn) => {
  if (!DEBUG_POPUPS) return fn?.();
  console.group(label);
  try { return fn?.(); } finally { console.groupEnd(); }
};

// Replace the existing checkDrawdown with this edge‑triggered + lock/unlock version
async function checkDrawdown(
  trades,
  drawdownLimitPct,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  setDrawdownAmount,
  setDrawdownPct
) {
  try {
    if (
      !Array.isArray(trades) ||
      !Number.isFinite(Number(drawdownLimitPct)) ||
      Number(drawdownLimitPct) <= 0
    ) {
      return;
    }

    const todayISO  = getTodayLocalISO();
    const POPUP_ID  = "drawdownWarning";
    const SHOWN_KEY = "DrawdownPopupShownDate"; // kept for backward compatibility / migration

    // ---- helpers -----------------------------------------------------------
    const toNumber = (v) => {
      if (typeof v === "number") return v;
      if (v === null || typeof v === "undefined") return NaN;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
      try { return parseCurrency(v); } catch { return NaN; }
    };

    const rowTimestamp = (row) => {
      const d = row?.["Date"];
      let base;
      if (!d) base = new Date(0);
      else if (typeof d === "string") {
        if (d.includes("-")) base = new Date(d); // "YYYY-MM-DD" or ISO
        else if (d.includes("/")) {
          const [mm, dd, yyyy] = d.split("/").map(Number);
          base = new Date(yyyy || 1970, (mm || 1) - 1, dd || 1);
        } else base = new Date(d);
      } else if (d?.seconds) base = new Date(d.seconds * 1000);
      else base = new Date(d);

      let secs = 0;
      const t = row?.["Exit Time"] || row?.["Entry Time"] || "";
      if (t) {
        try { secs = timeStringToSeconds(t); } catch { secs = 0; }
      }
      return base.getTime() + (Number.isFinite(secs) ? secs * 1000 : 0);
    };

    const getBalance = (row) => {
      const rb = toNumber(row?.["Running Balance"]);
      if (Number.isFinite(rb) && rb > 0) return rb;
      const ab = toNumber(row?.["Actual Balance"]);
      if (Number.isFinite(ab) && ab > 0) return ab;
      return NaN;
    };
    // -----------------------------------------------------------------------

    // Deterministic sort
    const rowsSorted = [...trades].sort((a, b) => {
      const na = Number(a?.tradeNum ?? a?.["Trade #"]);
      const nb = Number(b?.tradeNum ?? b?.["Trade #"]);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return rowTimestamp(a) - rowTimestamp(b);
    });

    // Equity series (positive numbers only)
    const series = rowsSorted
      .map(getBalance)
      .filter((n) => Number.isFinite(n) && n > 0);

    // Include a configured Initial Balance as a potential historical peak
    const initial = toNumber(currentSettings?.["Initial Balance"]);
    if (Number.isFinite(initial) && initial > 0) series.push(initial);

    if (series.length === 0) return;

    // Last positive balance
    let last = NaN;
    for (let i = rowsSorted.length - 1; i >= 0; i--) {
      const b = getBalance(rowsSorted[i]);
      if (Number.isFinite(b) && b > 0) { last = b; break; }
    }
    if (!Number.isFinite(last) || last <= 0) return;

    const peak = Math.max(...series); // historical max
    if (!(peak > 0)) return;

    const ddAmount = +(peak - last).toFixed(2);
    const ddPct = Math.min(100, Math.max(0, ((peak - last) / peak) * 100));

    // keep UI state in sync
    if (typeof setDrawdownAmount === "function") setDrawdownAmount(ddAmount);
    if (typeof setDrawdownPct === "function")    setDrawdownPct(ddPct);

    // --- lock/unlock state pulled from settings
    const lockActive   = Boolean(currentSettings?.DrawdownLockActive);
    const lockPeak     = Number(currentSettings?.DrawdownLockPeak || 0);
    const lockStarted  = String(currentSettings?.DrawdownLockStartedDate || "");
    const shownDateRaw = String(currentSettings?.[SHOWN_KEY] || "");
    const overLimit    = ddPct >= Number(drawdownLimitPct);

    // --- MIGRATION: if a previous daily-gated show happened (shown date present)
    // and we are still over limit but no lock yet, silently adopt into the new lock
    if (overLimit && !lockActive && shownDateRaw) {
      const patch = {
        DrawdownLockActive: true,
        DrawdownLockPeak: peak,
        DrawdownLockStartedDate: shownDateRaw,
      };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings?.(prev => ({ ...prev, ...patch }));
      return; // do not re-show
    }

    // --- EDGE-TRIGGERED LOGIC ---
    if (overLimit) {
      if (!lockActive) {
        // crossing from below → above: fire once and lock
        queuePopup(POPUP_ID, {
          drawdownLimitPct: Number(drawdownLimitPct),
          drawdownAmount: ddAmount,
          drawdownPct: ddPct,
        });

        const patch = {
          [SHOWN_KEY]: todayISO,          // optional: keep for analytics/visibility
          DrawdownLockActive: true,
          DrawdownLockPeak: peak,
          DrawdownLockStartedDate: todayISO,
        };
        await updateSettingsFunc(userId, accountId, patch);
        setLocalSettings?.(prev => ({ ...prev, ...patch }));
      }
      // if locked and still over the limit → do nothing (no re-trigger)
      return;
    }

    // below the limit → unlock (and clear old daily gate if present)
    if (lockActive || shownDateRaw) {
      const patch = {
        [SHOWN_KEY]: "",
        DrawdownLockActive: false,
        DrawdownLockPeak: 0,
        DrawdownLockStartedDate: "",
      };
      await updateSettingsFunc(userId, accountId, patch);
      setLocalSettings?.(prev => ({ ...prev, ...patch }));
    }
  } catch (e) {
    console.error("checkDrawdown error:", e);
  }
}




async function checkDailyProfitTarget(trades, dailyGoal, currentSettings, updateSettingsFunc, userId, accountId, setLocalSettings) {
  const today = getTodayLocalISO();
  const popupId = "dailyGoalPopup";
  const shownDateKey = "DailyGoalPopupShownDate";
  const shownGoalKey = "DailyGoalPopupShownForGoal";

  // --- EARLY EXIT GUARD ---
  if (currentSettings[shownDateKey] === today) {
    return; // Already shown today, never queue again!
  }

  const todayTrades = trades.filter(t => (t["Date"] || "").startsWith(today));
  let totalProfit = todayTrades.reduce((sum, t) => sum + (Number(t["Actual Reward"]) || 0), 0);

  if (totalProfit >= dailyGoal && dailyGoal > 0 && (Number(currentSettings[shownGoalKey] || 0) !== dailyGoal)) {
  queuePopup("dailyGoalPopup", {
    message: "Congratulations on hitting your daily goal!",
    dailyGoal: dailyGoal  // <-- use the parameter
  });
  const newSettings = { ...currentSettings, [shownDateKey]: today, [shownGoalKey]: dailyGoal };
  await updateSettingsFunc(userId, accountId, newSettings);
  setLocalSettings(newSettings);
} else if (totalProfit < dailyGoal && currentSettings[shownDateKey] === today && Number(currentSettings[shownGoalKey] || 0) === dailyGoal) {
    // Reset if goal is no longer met (e.g., daily goal was increased)
    const newSettings = { ...currentSettings, [shownDateKey]: "", [shownGoalKey]: 0 };
    await updateSettingsFunc(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}


async function checkWin10NoRulesPopup(trade, settings, updateSettings, userId, accountId, setLocalSettings, queuePopup) {
  const today = (new Date()).toISOString().slice(0, 10);
  const key = "Win10NoRulesPopupShownDate";

  // Check for win, 10%+ growth, didn't follow rules, and not shown today
  if (
    trade["Win"] === true &&
    Number(trade["Actual Percent Growth"]?.replace('%','')) >= 10 &&
    (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) === "No" &&
    settings[key] !== today
  ) {
    queuePopup("win10NoRulesPopup", {
      message: "Winning BIG is always exciting!\n\n However you want to be careful. Having a big win without following your rules can be dangerous for your mindset.\n\nContinuing to do this could rewire your brain to seek dopamine hits instead of the true accomplishment of following your process.\n\nThis is another way to easily lose control and go on tilt.\n\nTreat this as a neutral event, and be mindful not to get hooked by big wins that happen outside your plan.\n\nDiscipline > excitement!",
    });
    const newSettings = { ...settings, [key]: today };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (
    // Reset if not matching and was previously set
    (trade["Win"] !== true || Number(trade["Actual Percent Growth"]?.replace('%','')) < 10 || (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) !== "No") &&
    settings[key] === today
  ) {
    const newSettings = { ...settings, [key]: "" };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}

async function checkWin10WithRulesPopup(trade, settings, updateSettings, userId, accountId, setLocalSettings, queuePopup) {
  const today = (new Date()).toISOString().slice(0, 10);
  const key = "Win10WithRulesPopupShownDate";

  // Check for win, 10%+ growth, followed rules, and not shown today
  if (
    trade["Win"] === true &&
    Number(trade["Actual Percent Growth"]?.replace('%','')) >= 20 &&
    (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) === "Yes" &&
    settings[key] !== today
  ) {
    queuePopup("win10WithRulesPopup", {
      message: "Congratulations! You followed your rules and had a BIG win! \n\nDon't get over confident, stay consistent and continue following your rules. \n\nContinue being Awesome!",
      confetti: true
    });
    const newSettings = { ...settings, [key]: today };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  } else if (
    (trade["Win"] !== true || Number(trade["Actual Percent Growth"]?.replace('%','')) < 20 || (trade["Did I follow my Rules?"] || trade["Did I follow my Rules"]) !== "Yes") &&
    settings[key] === today
  ) {
    const newSettings = { ...settings, [key]: "" };
    await updateSettings(userId, accountId, newSettings);
    setLocalSettings(newSettings);
  }
}



  async function checkLessonsLearnedStreak(
    trades,
    currentSettings,
    updateSettingsFunc,
    userId,
    accountId,
    setLocalSettings
  ) {
    const todayISO = getTodayLocalISO();

    // Count how many of today's rows have a lesson logged (text present OR LogLesson true)
    const lessonsToday = (trades || []).filter(t => {
      const d = t["Date"];
      let isToday = false;
      if (!d) return false;
      if (typeof d === "string") {
        isToday = d.slice(0, 10) === todayISO || d.startsWith(todayISO);
      } else if (d?.seconds) {
        isToday = new Date(d.seconds * 1000).toISOString().slice(0, 10) === todayISO;
      } else {
        try { isToday = new Date(d).toISOString().slice(0, 10) === todayISO; } catch { isToday = false; }
      }
      if (!isToday) return false;

      const loggedFlag = t["LogLesson"] === true || String(t["LogLesson"]).toLowerCase() === "true";
      return loggedFlag; // only count lessons that were explicitly logged
    }).length;

    if (lessonsToday <= 3) {
      // Optional: if previously shown today but the count fell below 3 (e.g., user edited a trade), clear the flag
      if (currentSettings["LessonsLearnedStreakWarningShownDate"] === todayISO) {
        const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: "" };
        await updateSettingsFunc(userId, accountId, next);
        setLocalSettings(next);
      }
      return;
    }

    // Fire once per day when the threshold (>= 3) is reached
    if (currentSettings["LessonsLearnedStreakWarningShownDate"] !== todayISO) {
      queuePopup("lessonsLearnedStreakWarning", { lessonsToday });
      const next = { ...currentSettings, LessonsLearnedStreakWarningShownDate: todayISO };
      await updateSettingsFunc(userId, accountId, next);
      setLocalSettings(next);
    }
  }


async function checkRiskOfRuin(
  lossProjection,
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  const todayISO = getTodayLocalISO();
  const popupId  = "riskOfRuinWarning";
  const shownKey = "RiskOfRuinWarningShownDate";

  // ── NEW: If max loss blown was shown today, skip ROR entirely
  const blownShownToday = String(currentSettings["MaxLossLimitBlownShownDate"] || "") === todayISO;
  if (blownShownToday) return;

    // ✅ Work for both Prop and Retail by falling back to retail fields
    const propInit = Number(
      currentSettings["Prop Initial Balance"] ?? currentSettings["Initial Balance"] ?? 0
    );
    const propRB   = Number(
      currentSettings["Prop Running Balance"] ?? currentSettings["Running Balance"] ?? 0
    );
  if (!(propInit > 0)) return;

  const threshold = propInit * 0.25; // 25% of prop initial

  if (propRB <= threshold && currentSettings[shownKey] !== todayISO) {
    queuePopup(popupId, { propInitial: propInit, propRunning: propRB, threshold });
    const next = { ...currentSettings, [shownKey]: todayISO };
    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings(next);
  } else if (propRB > threshold && currentSettings[shownKey] === todayISO) {
    const next = { ...currentSettings, [shownKey]: "" };
    await updateSettingsFunc(userId, accountId, next);
    setLocalSettings(next);
  }
}


// Replace the existing checkTwoLossAfterGoal with this version
async function checkTwoLossAfterGoal(
  trades,
  settings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings,
  queuePopupFunc
) {
  const todayISO = getTodayLocalISO(); // ← local date (fix #1)
  const shownKey = "TwoLossAfterGoalWarningShownDate";
  const dailyGoal = Number(settings["Daily Goal"] || 0);
  if (!(dailyGoal > 0)) return; // avoid false positives when goal is 0

  // Robust "is today?" (same idea as your other helpers)
  const isTodayRow = (d) => {
    if (!d) return false;
    if (typeof d === "string") {
      if (d.includes("-")) return d.startsWith(todayISO); // "YYYY-MM-DD" or ISO
      if (d.includes("/")) {
        const [mm, dd, yyyy] = d.split("/");
        const iso = `${yyyy}-${String(mm).padStart(2,"0")}-${String(dd).padStart(2,"0")}`;
        return iso === todayISO;
      }
      const dt = new Date(d);
      return !isNaN(dt) && dt.toISOString().slice(0,10) === todayISO;
    }
    if (d?.seconds) return new Date(d.seconds * 1000).toISOString().slice(0,10) === todayISO;
    try { return new Date(d).toISOString().slice(0,10) === todayISO; } catch { return false; }
  };

  // Number + tolerant Loss bool
  const toNumber = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    try { return Number(String(v).replace(/[^\d.-]/g, "")) || 0; } catch { return 0; }
  };
  const isLoss = (v) => v === true || String(v).toLowerCase() === "true" || String(v).toLowerCase() === "yes" || v === 1;

  // Deterministic sort (like checkDrawdown)
  const rowTimestamp = (row) => {
    const d = row?.["Date"];
    let base;
    if (!d) base = new Date(0);
    else if (typeof d === "string") {
      if (d.includes("-")) base = new Date(d);
      else if (d.includes("/")) {
        const [mm, dd, yyyy] = d.split("/").map(Number);
        base = new Date(yyyy || 1970, (mm || 1) - 1, dd || 1);
      } else base = new Date(d);
    } else if (d?.seconds) base = new Date(d.seconds * 1000);
    else base = new Date(d);

    let secs = 0;
    const t = row?.["Exit Time"] || row?.["Entry Time"] || "";
    if (t) {
      const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([APap][Mm])?$/);
      if (m) {
        let hh = Number(m[1]), mm = Number(m[2]), ss = Number(m[3] || 0);
        const ap = m[4];
        if (ap) {
          if (/pm/i.test(ap) && hh !== 12) hh += 12;
          if (/am/i.test(ap) && hh === 12) hh = 0;
        }
        secs = hh * 3600 + mm * 60 + ss;
      }
    }
    return base.getTime() + secs * 1000;
  };

  const todayTrades = (trades || []).filter(r => isTodayRow(r["Date"]));
  todayTrades.sort((a, b) => {
    const na = Number(a?.tradeNum ?? a?.["Trade #"]);
    const nb = Number(b?.tradeNum ?? b?.["Trade #"]);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return rowTimestamp(a) - rowTimestamp(b);
  });

  // Find when goal was first reached today
  let runningProfit = 0;
  let goalReachedAtIndex = -1;
  for (let i = 0; i < todayTrades.length; i++) {
    runningProfit += toNumber(todayTrades[i]["Actual Reward"]);
    if (runningProfit >= dailyGoal && goalReachedAtIndex === -1) {
      goalReachedAtIndex = i;
    }
  }
  if (goalReachedAtIndex === -1) return; // never reached goal today

  const tradesAfterGoal = todayTrades.slice(goalReachedAtIndex + 1);

  // NEW: trigger on any later pair of consecutive losses (not necessarily the next two)
  let twoLossesAfter = false;
  let streak = 0;
  for (const t of tradesAfterGoal) {
    if (isLoss(t["Loss"])) {
      streak += 1;
      if (streak >= 2) { twoLossesAfter = true; break; }
    } else {
      streak = 0;
    }
  }

  if (twoLossesAfter && settings[shownKey] !== todayISO) {
    queuePopupFunc("twoLossAfterGoalWarning", {
      message:
        "You have taken 2 losses after reaching your daily goal.\n\nYou could easily go on tilt if you continue to lose trying to get it back. \n\nConsider stopping for the day to protect your gains."
    });
    await updateSettingsFunc(userId, accountId, { [shownKey]: todayISO });
    setLocalSettings(prev => ({ ...prev, [shownKey]: todayISO }));
  } else if (!twoLossesAfter && settings[shownKey] === todayISO) {
    await updateSettingsFunc(userId, accountId, { [shownKey]: "" });
    setLocalSettings(prev => ({ ...prev, [shownKey]: "" }));
  }
}


function formatThreeLossMessage(line) {
  if (!line) return null;

  // Define the phrases and their corresponding styles
  const highlights = {
    "Take a break!": "font-bold text-red-500 dark:text-red-500",
    "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
    "Continue being Awesome!": "font-bold text-green-600 dark:text-green-400", // ⬅️ add this
    "BIG": "font-bold text-green-600 dark:text-green-400" // ⬅️ add this
  };

  // Escape special regex chars in phrases, then join
  const escaped = Object.keys(highlights)
    .map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const regex = new RegExp(`(${escaped})`, "g");

    return line.split(regex).map((part, i) =>
      highlights[part]
        ? <span key={i} className={highlights[part]}>{part}</span>
        : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    );
  }


function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">
                On Tilt
              </div>
              <p className="mt-5  text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5  text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>

              <p className="mt-6 text-[14px] font-bold leading-relaxed">
                What to do:  
              </p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>

            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function Tooltip({
  children,
  content,
  variant = "neutral",
  backdrop = false,   // ← NEW: dim the page behind the tooltip
  closeOnEsc = true,  // ← NEW: press Esc to close when backdrop is on
  lockScroll = true,  // ← NEW: lock body scroll when backdrop is on
}) {
  const id = useId();
  const [open, setOpen] = useState(false);


    // Close on ESC when using a backdrop
    useEffect(() => {
      if (!(open && backdrop && closeOnEsc)) return;
      const onKeyDown = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, backdrop, closeOnEsc]);
  
    // Optionally lock body scroll while backdrop is visible
    useEffect(() => {
      if (!(open && backdrop && lockScroll)) return;
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }, [open, backdrop, lockScroll]);


  const palette = {
    neutral: {
      panel: "bg-gray-900/95 text-white ring-white/10",
      arrow: "bg-gray-900/95",
      focus: "focus:ring-yellow-500", // trigger outline color
    },
    yellow: {
      panel: "bg-yellow-600/95 text-white ring-yellow-300/30",
      arrow: "bg-yellow-600/95",
      focus: "focus:ring-yellow-500",
    },
    red: {
      panel: "bg-red-800/95 text-white ring-red-300/30",
      arrow: "bg-red-800/95",
      focus: "focus:ring-red-500",
    },
    blue: {
      panel: "bg-slate-900/95 text-white ring-blue-300/30",
      arrow: "bg-slate-900/95",
      focus: "focus:ring-blue-500",
    },
  }[variant] || {
    panel: "bg-gray-900/95 text-white ring-white/10",
    arrow: "bg-gray-900/95",
    focus: "focus:ring-yellow-500",
  };

    const hoverClasses = backdrop
      ? ""
      : "group-hover:opacity-100 group-hover:scale-100 group-focus-within:opacity-100 group-focus-within:scale-100";
  
    return (
      <span
        className="relative inline-flex items-center group"
        onMouseEnter={() => !backdrop && setOpen(true)}
        onMouseLeave={() => !backdrop && setOpen(false)}
      >

      {/* Trigger (focusable + tap/click for mobile) */}
      <span
        tabIndex={0}
        aria-describedby={id}
        aria-expanded={open ? "true" : "false"}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
        onBlur={() => { if (!backdrop) setOpen(false); }}
        className={`underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}
      >
        {children}
      </span>

      {/* Tooltip panel */}
            <span
              id={id}
              role={backdrop ? "dialog" : "tooltip"}
              aria-modal={backdrop ? "true" : undefined}
              className={`
                select-text absolute ${backdrop ? "z-[70]" : "z-[60]"}

          bottom-full left-1/2 -translate-x-1/2 mb-2
          max-w-[min(90vw,28rem)] w-max break-words
          rounded-lg px-4 py-3 text-[13px] leading-relaxed font-normal
          shadow-2xl ring-1 backdrop-blur-sm
          transition opacity-0 scale-95 pointer-events-none
          ${palette.panel}
                    ${open ? "opacity-100 scale-100 pointer-events-auto" : ""}
                    ${hoverClasses}
        `}
      >
        {/* Allow string or JSX for content */}
        {typeof content === "string" ? <span className="block">{content}</span> : content}

        {/* Arrow */}
        <span
          aria-hidden="true"
          className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow`}
        />
      </span>

            {/* Backdrop scrim rendered at <body> so it covers the whole viewport */}
            {open && backdrop && createPortal(
              <div
                className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[1px]"
                onClick={() => setOpen(false)}
                aria-hidden="true"
              />,
              document.body
            )}
    </span>
  );
}

// Normalize to "YYYY-MM-DD"
 // new: always produce a LOCAL YYYY-MM-DD key, including Firestore Timestamps
 function localISODay(d) {
   const dt = new Date(d);
   const tz = dt.getTimezoneOffset() * 60000; // minutes → ms
   return new Date(dt - tz).toISOString().slice(0, 10);
 }

 function normalizeDateKey(v) {
   if (!v) return "";
   if (typeof v === "string") {
     const s = v.trim();
     if (s.includes("/")) {                 // "MM/DD/YYYY" → local ISO
       const [mm, dd, yyyy] = s.split("/");
       return `${yyyy}-${mm.padStart(2,"0")}-${dd.padStart(2,"0")}`;
     }
     return s.slice(0, 10);                 // "YYYY-MM-DD..." already fine
   }
   if (v?.seconds) return localISODay(v.seconds * 1000); // Firestore TS → local day
   try { return localISODay(v); } catch { return ""; }
 }


// --- Two Loss warning (static text) ---
const TWO_LOSS_WARNING_TEXT = `You've had 2 losses in a row.

Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?

Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.

Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.

You've got this. Apply what you learned in the next one!`;


async function checkMaxLossLimitBlown(
  currentSettings,
  updateSettingsFunc,
  userId,
  accountId,
  setLocalSettings
) {
  try {
    const today =
      (typeof getTodayLocalISO === "function")
        ? getTodayLocalISO()
        : new Date().toISOString().slice(0, 10);

    const shownKey = "MaxLossLimitBlownShownDate";

    // Use the full account figures for the prop max-loss rule:
    const initial = Number(currentSettings["Initial Balance"] || 0);
    const maxLoss = Number(currentSettings["Maximum Loss Limit"] || currentSettings["Max Loss Limit"] || 0);
    const rb      = Number(currentSettings["Running Balance"] || 0);

    if (!(initial > 0 && maxLoss > 0)) return;

    // Prop rule: account is blown when RB <= (Initial - MaxLoss)
    const threshold = initial - maxLoss;
    const blown = rb <= threshold;

    if (blown && currentSettings[shownKey] !== today) {
      // Show the popup with values to render
      queuePopup("maxLossLimitBlown", { initial, maxLoss, rb });

      // Mark as shown (partial update so nothing else gets overwritten)
      await updateSettingsFunc(userId, accountId, { [shownKey]: today });
      setLocalSettings?.(prev => ({ ...prev, [shownKey]: today }));
    } else if (!blown && currentSettings[shownKey] === today) {
      // Clear flag so it can re-trigger if the status changes
      await updateSettingsFunc(userId, accountId, { [shownKey]: "" });
      setLocalSettings?.(prev => ({ ...prev, [shownKey]: "" }));
    }
  } catch (e) {
    console.error("checkMaxLossLimitBlown error:", e);
  }
}


  // ---- Projection Logic ----
  function getCompoundingProjection({
    balance, goal, riskPercent, riskMode, riskAmount, stopLoss, takeProfit, maxContracts, minContracts, pricePerTick,
  }) {
    let trades = [];
    let currentBalance = balance;
    let tradeCount = 0;
    while (currentBalance < goal && tradeCount < 10000) {
        const { dollars: riskDollar } = getRiskSpec(
          { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
          currentBalance
        );
      const perContractRisk = stopLoss * pricePerTick;
      let contracts = perContractRisk > 0 ? Math.floor(riskDollar / perContractRisk) : 0;
      contracts = Math.max(contracts, minContracts);
      contracts = Math.min(contracts, maxContracts);
      if (contracts < 1) contracts = minContracts;
      const profitPerContract = takeProfit * pricePerTick;
      const tradeProfit = contracts > 0 ? contracts * profitPerContract : 0;
      trades.push({
        trade: tradeCount + 1,
        balance: currentBalance,
        riskDollar,
        contracts,
        totalRisk: contracts > 0 ? contracts * perContractRisk : perContractRisk * minContracts,
        tradeProfit,
        nextBalance: contracts > 0 ? currentBalance + tradeProfit : currentBalance,
        underfunded: perContractRisk > riskDollar,
      });
      currentBalance = contracts > 0 ? currentBalance + tradeProfit : currentBalance;
      tradeCount++;
      if (tradeCount > 10000) break;
      if (currentBalance >= goal) break;
    }
    return { trades, tradeCount: trades.length };
  }

// Recompute just the Summary + Projections from a settings snapshot (or override).
const runSummaryOnly = (override = {}) => {
  const s = { ...settings, ...override };

  const product       = s["Product"];
  const runningBal    = Number(s["Running Balance"] || 0);
  const goal          = Number(s["Account Goal"] || 0);
  const riskPercent   = Number(s["Risk Percentage"] || 0);
  const riskMode      = String(settings["Risk Mode"] || "Percent");
  const riskAmount    = Number(settings["Risk Amount"] || 0);
  const minContracts  = Number(s["Minimum Contracts"] || 1);
  const maxContracts  = Number(s["Maximum Contracts"] || 1);
  
  // ADDED: Make sure entryPrice is pulled from settings
  const entryPrice    = Number(s["Entry Price"] || 0); 

  const tickSize      = getTickSizeForProduct(product) || 0.25;
  const pricePerTick  = getPricePerTick(product);

  // --- NEW: DYNAMIC EXIT LOGIC ---
  const slMode = s["Stop Loss Mode"] || "Ticks";
  const tpMode = s["Take Profit Mode"] || "Ticks";
  const rawSL  = Number(s["Stop Loss"] || 0);
  const rawTP  = Number(s["Take Profit"] || 0);

  // DECLARE ONCE using let
  let stopLoss = rawSL;
  if (slMode === "Price" && entryPrice > 0) {
      stopLoss = tickSize > 0 ? Math.abs(entryPrice - rawSL) / tickSize : 0;
  }
  
  let takeProfit = rawTP;
  if (tpMode === "Price" && entryPrice > 0) {
      takeProfit = tickSize > 0 ? Math.abs(rawTP - entryPrice) / tickSize : 0;
  }
  // -------------------------------
  const { dollars: riskDollars, percent: riskPctFromBase } = getRiskSpec(s, runningBal);
  const perCtrRisk    = stopLoss * pricePerTick;

  // suggested contracts (clamped to min/max)
  let suggested = perCtrRisk > 0 ? Math.floor(riskDollars / perCtrRisk) : 0;
  if (suggested < minContracts) suggested = minContracts;
  if (suggested > maxContracts) suggested = maxContracts;
  if (suggested * perCtrRisk > riskDollars) suggested = 0;

  const usedContracts = suggested > 0 ? suggested : minContracts;
  const totalRisk     = usedContracts * stopLoss   * pricePerTick;
  const targetReward  = usedContracts * takeProfit * pricePerTick;

  let riskRewardRatio = "";
  if (takeProfit && stopLoss) {
    const rr = takeProfit / stopLoss;
    riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
  }

  

  const maxStopLossSize      = pricePerTick > 0 ? Math.floor(riskDollars / pricePerTick) : 0;            // 1 contract
  const maxStopLossSizeForMin = (pricePerTick > 0 && minContracts > 0)
    ? Math.floor(riskDollars / (pricePerTick * minContracts))
    : 0;

  // Update the Summary panel
  setCalculatedSummary({
    pricePerTick,
    riskRewardRatio,
    // Store dollars in percentOfBalance (as in your main path),
    // and also store the derived ≈% for display in $ mode.
    percentOfBalance: riskDollars,        // always dollars
    riskPercent,                          // input %
    riskPctFromBase,                      // ≈ % when in $ mode
    stopLoss,
    takeProfit,
    contracts: suggested,
    minContracts,
    totalRisk,
    targetReward,
    maxStopLossSize,          // for 1 contract
    maxStopLossSizeForMin,    // for minContracts
  });


  // Update Win & Loss projections (these drive “# trades to goal”)
  setWinProjection(getCompoundingProjection({
   balance: runningBal,
   goal,
   riskPercent,
   riskMode: settings["Risk Mode"] || "Percent",
   riskAmount: Number(settings["Risk Amount"] || 0),
   stopLoss,
   takeProfit,
   maxContracts,
   minContracts,
   pricePerTick,
  }));

   setLossProjection(getLossProjection({
   balance: runningBal,
   goal,
   riskPercent,
   riskMode: settings["Risk Mode"] || "Percent",
   riskAmount: Number(settings["Risk Amount"] || 0),
   stopLoss,
   takeProfit,
   maxContracts,
   minContracts,
   pricePerTick,
   }));
};


function getLossProjection({
  balance,
  riskPercent,
    riskMode,
  riskAmount,
  stopLoss,
  maxContracts,
  minContracts,
  pricePerTick
}) {
  const trades = [];
  const perCtrRisk = Number(stopLoss) * Number(pricePerTick);
  const minCtr     = Math.max(1, Number(minContracts) || 1);
  const maxCtr     = Math.max(minCtr, Number(maxContracts) || minCtr);

  // Guard: need valid $/tick and stop loss
  if (!Number.isFinite(perCtrRisk) || perCtrRisk <= 0) {
    return { trades: [], tradeCount: 0, suggestedContracts: 0 };
  }

  let currentBalance = Number(balance) || 0;
  let tradeCount     = 0;
  let prevContracts  = Infinity; // enforce non-increasing lots/contracts

  while (currentBalance > 0 && tradeCount < 10000) {
      const { dollars: riskDollar } = getRiskSpec(
        { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
        currentBalance
      );

    // % risk sizing for this row
    let contracts = perCtrRisk > 0 ? Math.floor(riskDollar / perCtrRisk) : 0;
    contracts = Math.max(minCtr, Math.min(maxCtr, contracts));

    // enforce non-increasing sizing as balance declines
    if (contracts > prevContracts) contracts = prevContracts;

    const lossPerTrade = contracts * perCtrRisk;

    // stop if the next full loss wouldn’t be covered
    if (!(lossPerTrade > 0) || currentBalance < lossPerTrade) break;

    trades.push({
      trade: tradeCount + 1,
      balanceBefore: currentBalance,
      contracts,
      loss: lossPerTrade,
      balanceAfter: Math.max(0, +(currentBalance - lossPerTrade).toFixed(2)),
    });

    currentBalance = Math.max(0, currentBalance - lossPerTrade);
    prevContracts  = contracts;
    tradeCount++;
  }

  // starting suggested contracts (what the Summary typically shows)
    const { dollars: riskDollar0 } = getRiskSpec(
      { "Risk Mode": riskMode, "Risk Percentage": riskPercent, "Risk Amount": riskAmount },
      currentBalance
    );
    let suggested = perCtrRisk > 0 ? Math.floor(riskDollar0 / perCtrRisk) : 0;
  suggested = Math.max(minCtr, Math.min(maxCtr, suggested));

  return { trades, tradeCount: trades.length, suggestedContracts: suggested };
}

// ---- Dynamic Auto-Calculation Logic ----
  const canCalculate = useMemo(() => {
    // 1. Product is always required
    if (!settings["Product"]) return false;

    // 2. Risk Amount/Percentage must be > 0
    const riskVal = settings["Risk Mode"] === "$" 
      ? Number(settings["Risk Amount"] || settings["Risk Dollar Amount"]) 
      : Number(settings["Risk Percentage"]);
    if (!riskVal || riskVal <= 0) return false;

    // 3. Stop Loss must be present and > 0
    const stopLoss = Number(settings["Stop Loss"]);
    if (isNaN(stopLoss) || stopLoss <= 0) return false;

    // 4. Target (Take Profit) must be present and > 0
    const takeProfit = Number(settings["Take Profit"]);
    if (isNaN(takeProfit) || takeProfit <= 0) return false;

    return true;
  }, [settings]);

  // Trigger non-saving calculation when Trade Setup and required fields are updated
useEffect(() => {
  if (!initialSettings || !canCalculate) return;

  const delayDebounceFn = setTimeout(() => {
    runSummaryOnly();
    
    // Restore the background auto-save functionality:
    updateSettings(userId, accountId, settings).catch(err => 
      console.error("Auto-save failed:", err)
    );
  }, 300);

  return () => clearTimeout(delayDebounceFn);
}, [
  canCalculate,
  settings["Product"],
  settings["Entry Price"],
  settings["Stop Loss"],
  settings["Take Profit"],
  settings["Risk Mode"],
  settings["Risk Percentage"],
  settings["Risk Amount"],
  settings["Risk Dollar Amount"],
  settings["Stop Loss Mode"],
  settings["Take Profit Mode"],
  settings["Running Balance"],
  settings["Account Goal"]
]);

  // ---- Field Change Handlers ----
const numericFields = [
  "Initial Balance", "Maximum Contracts", "Stop Loss", "Daily Goal",
  "Minimum Contracts", "Take Profit", "Daily Loss Limit", "Account Goal",
  "Running Balance", "Risk Percentage", "Max Trades Per Day",
  "Max Loss Trades Per Day", "Drawdown Limit %", "Risk Amount", "IdealSL"
];



const handleSettingChange = (key, value) => {
  popupsAllowedRef.current = false; // ensure queue is OFF while editing
  setSettings(prev => {
    const next = { ...prev };

    if (RISK_KEYS.has(key)) {
      // Keep the raw string while the user types (allows clearing)
      next[key] = value;
    } else if (numericFields?.includes?.(key)) {
      // Normal numeric behavior for all other fields
      next[key] = value === "" ? "" : Number(value);
    } else {
      next[key] = value;
    }

    if (key === "Initial Balance") {
      const n = Number(value);
      next["Running Balance"] = Number.isFinite(n) ? n : 0;
    }
    return next;
  });
  // Clear summary while editing so intermediates like "" don't recalc
  setCalculatedSummary?.(null);
};





  const handleJournalChange = (key, value) => setJournal(prev => ({ ...prev, [key]: value }));


  function getRiskSpec(s, base) {
    const raw  = String(s["Risk Mode"] ?? "%").trim().toLowerCase();
    const mode = (raw === "$" || raw === "dollar") ? "dollar" : "percent";
    const pct  = Number(s["Risk Percentage"] ?? 0);
    const amt  = Number(s["Risk Amount"] ?? s["Risk Dollar Amount"] ?? 0);
    const dollars = mode === "dollar"
      ? Math.max(0, amt)
      : Math.max(0, base) * (pct / 100);
    const percent = base > 0 ? (dollars / base) * 100 : 0;
    return { mode, pct, amt, dollars, percent };
  }


  // ---- Summary Calculation ----
  const handleCalculate = async (manual = false) => {
    if (manual) setManualCalculate(true);
    setSavingSettings(true);
    let freshSettings = { ...settings }; 
    try {
      const toSave = Object.entries(settings).map(([k, v]) => [k, v]);
      await updateSettings(userId, accountId, settings);
      freshSettings = await getSettings(userId, accountId);
      const product        = freshSettings["Product"];
      const pricePerTick   = getPricePerTick(product);
      const tickSize       = getTickSizeForProduct(product) || 0.25;
      const runningBalance = Number(freshSettings["Running Balance"] || 0);
      const initialBalance = Number(freshSettings["Initial Balance"] || 0);
      const riskPercent    = Number(freshSettings["Risk Percentage"] || 0);
      const riskMode       = String(freshSettings["Risk Mode"] || "%");
      const riskAmount     = Number(freshSettings["Risk Amount"] || 0);
      const entryPrice     = Number(freshSettings["Entry Price"] || 0);
      
      const maxContracts = Number(freshSettings["Maximum Contracts"] ?? defaultSettings["Maximum Contracts"] ?? 10);
      const minContracts   = Number(freshSettings["Minimum Contracts"] || 1);
      const goal           = Number(freshSettings["Account Goal"] || 0);

      // --- NEW: DYNAMIC EXIT LOGIC ---
      const slMode = freshSettings["Stop Loss Mode"] || "Ticks";
      const tpMode = freshSettings["Take Profit Mode"] || "Ticks";
      const rawSL  = Number(freshSettings["Stop Loss"] || 0);
      const rawTP  = Number(freshSettings["Take Profit"] || 0);

      let stopLoss = rawSL;
      if (slMode === "Price" && entryPrice > 0) {
          stopLoss = tickSize > 0 ? Math.abs(entryPrice - rawSL) / tickSize : 0;
      }
      
      let takeProfit = rawTP;
      if (tpMode === "Price" && entryPrice > 0) {
          takeProfit = tickSize > 0 ? Math.abs(rawTP - entryPrice) / tickSize : 0;
      }
      // -------------------------------



      const { dollars: riskDollars, percent: riskPctFromBase } =
       getRiskSpec(freshSettings, runningBalance);
      let riskRewardRatio = "";
      if (takeProfit && stopLoss) {
        const rr = takeProfit / stopLoss;
        riskRewardRatio = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
      }
      const perContractRisk = stopLoss * pricePerTick;
      let contracts = perContractRisk > 0 ? Math.floor(riskDollars / perContractRisk) : 0;
      contracts = Math.max(contracts, minContracts);
      contracts = Math.min(contracts, maxContracts);
      if (contracts * perContractRisk > riskDollars) {
        contracts = Math.floor(riskDollars / perContractRisk);
      }
      contracts = Math.max(contracts, minContracts);
      if (contracts * perContractRisk > riskDollars) contracts = 0;

      let totalRisk = contracts > 0
        ? contracts * perContractRisk
        : minContracts * perContractRisk;
      let targetReward = contracts > 0
        ? contracts * pricePerTick * takeProfit
        : minContracts * pricePerTick * takeProfit;
      let maxStopLossSize = pricePerTick > 0 ? Math.floor(riskDollars / pricePerTick) : 0;
            let maxStopLossSizeForMin =
               pricePerTick > 0 && minContracts > 0
                 ? Math.floor(riskDollars / (pricePerTick * minContracts))
                 : 0;


  const actualRiskPercent = runningBalance > 0 ? ((totalRisk / runningBalance) * 100).toFixed(2) : 0;

      setLastRiskPercent(riskPercent);
      setLastPercentOfBalance(riskDollars);
      setLastTotalRisk(totalRisk);
      setLastActualRiskPercent(actualRiskPercent);

            setCalculatedSummary({
              pricePerTick,
              riskRewardRatio,
              percentOfBalance: riskDollars,   // always dollars
              riskPercent,                     // input %
              riskPctFromBase,                 // ≈% when $ mode
               stopLoss,
               takeProfit,
               contracts,
               minContracts,
               totalRisk,
               targetReward,
               maxStopLossSize,
               maxStopLossSizeForMin,
             });


      setWinProjection(getCompoundingProjection({
        balance: runningBalance,
        goal,
        riskPercent,
        riskMode,
        riskAmount,
        stopLoss,
        takeProfit,
        maxContracts,
        minContracts,
        pricePerTick,
      }));



       setLossProjection(getLossProjection({
         balance: runningBalance,
         riskPercent,
         riskPercent,
         riskMode,
         riskAmount,
         stopLoss,
         maxContracts,
         minContracts,
         pricePerTick,
    }));


if (contracts === 0) {
  if (manual) setShowZeroContractsPopup(true); // This popup is not queued
} else {
  setShowZeroContractsPopup(false);
}
    } catch (err) {
      console.error("Settings save failed:", err);
    }
    if (manualCalculate) setManualCalculate(false);

    setSavingSettings(false);
  };

  // ---- Settings Load Effect ----
 useEffect(() => {
  const load = async () => {
    try {
      const settingsObj = await getSettings(userId, accountId);
      setSettings(settingsObj);
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };
  load();
}, [userId, accountId]);


 // --- AUTO-CALCULATE ON LOAD IF ALL FIELDS PRESENT ---
const AUTO_CALC_FIELDS = [
  "Product",
  "Initial Balance",
  "Maximum Contracts",
  "Stop Loss",
  "Daily Goal",
  "Minimum Contracts",
  "Take Profit",
  "Daily Loss Limit",
  "Account Goal",
  "Running Balance",
  "Risk Percentage"
];

const firstMountRef = useRef(true);


useEffect(() => {
  // Only care about the very first loaded settings
  if (!initialSettings || savingSettings || autoCalculatedOnce) return;

  // Require product to be selected on FIRST load only!
  if (!initialSettings["Product"] || initialSettings["Product"] === "") return;

  // Check all other required fields except Product for validity
  const allFieldsPresent = AUTO_CALC_FIELDS
    .filter(key => key !== "Product")
    .every(key =>
      typeof initialSettings[key] !== "undefined" &&
      initialSettings[key] !== "" &&
      Number(initialSettings[key]) > 0
    );

  if (allFieldsPresent) {
    // Run the calculation with first settings
    handleCalculate(false);
    setAutoCalculatedOnce(true);
  }
  // This effect will never run again
  // eslint-disable-next-line
}, [initialSettings, savingSettings, autoCalculatedOnce]);



useEffect(() => {
  // Only check after settings are loaded
  if (!settings || !userId || !accountId) return;
  if (!popupsAllowedRef.current) return;  // do not enqueue while editing settings
  const running = Number(settings["Running Balance"] || 0);
  const goal = Number(settings["Account Goal"] || 0);
  const today = getTodayLocalISO();

  if (goal > 0 && running >= goal && settings.AccountGoalReachedShownDate !== today) {
    queuePopup('accountGoalReached');
    const newSettings = { ...settings, AccountGoalReachedShownDate: today };
    updateSettings(userId, accountId, newSettings);
    setSettings(newSettings);
  }
  // Optionally: Reset on goal change or user/account change
  // eslint-disable-next-line
}, [settings, userId, accountId]);


useEffect(() => {
  if (
    calculatedSummary &&
    calculatedSummary.contracts !== undefined &&
    !userEditingSize
  ) {
    setJournal(prev => ({
      ...prev,
      Size: calculatedSummary.contracts
    }));
  }
}, [calculatedSummary]);

// --- Auto-sync Entry/Exit Prices to Journal ---
useEffect(() => {
    const entryPrice = Number(settings["Entry Price"] || 0);
    const tpVal = Number(settings["Take Profit"] || 0);
    let estTargetPrice = "";
    
    if (entryPrice > 0 && tpVal > 0) {
        if (settings["Take Profit Mode"] === "Price") {
            estTargetPrice = tpVal;
        } else {
            const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
            estTargetPrice = entryPrice + (tpVal * tickSize);
        }
    }

    setJournal(prev => {
        const next = { ...prev };
        let changed = false;
        
        if (settings["Entry Price"] !== undefined && settings["Entry Price"] !== "") {
            if (next["Entry Price"] !== settings["Entry Price"]) {
                next["Entry Price"] = settings["Entry Price"];
                changed = true;
            }
        }
        
        if (estTargetPrice !== "") {
            if (next["Exit Price"] !== estTargetPrice) {
                next["Exit Price"] = estTargetPrice;
                changed = true;
            }
        }
        
        return changed ? next : prev;
    });
}, [settings["Entry Price"], settings["Take Profit"], settings["Take Profit Mode"], settings["Product"]]);


// Show once per day, using *user-level* prefs only
const openedForTodayRef = useRef(false);

useEffect(() => {
  if (!userId) return;

  const today   = getTodayLocalISO();
  const enabled = ["yes","true","on","1"].includes(String(userPrefs?.["Pretrade Checklist"] ?? "").toLowerCase());
  const shown   = normalizeDateKey(
    userPrefs?.["PretradeChecklistShownDate"] ?? userPrefs?.["pretradeChecklistLastShown"] ?? ""
  );

  // If it's a new day relative to the last stamp, reset the gate so we can show again.
  if (shown !== today) {
    openedForTodayRef.current = false;
  } else {
    // Already stamped for today → make sure it's closed and mark handled.
    setShowChecklist(false);
    openedForTodayRef.current = true;
    return;
  }

  if (enabled && !openedForTodayRef.current) {
    setShowChecklist(true);
    openedForTodayRef.current = true; // prevent re-open if userPrefs reflows
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  userId,
  userPrefs?.["Pretrade Checklist"],
  userPrefs?.["PretradeChecklistShownDate"],  // canonical (user-level)
  userPrefs?.["pretradeChecklistLastShown"]   // legacy (read-only fallback)
]);



useEffect(() => {
  if (!userId || !accountId) return;

  let cancelled = false;

  (async () => {
    try {
      const rows = await fetchJournalRows(userId, accountId);
      if (!Array.isArray(rows) || cancelled) return;

      const entries = [...new Set(rows.map(r => r["Entry Strategy"]).filter(Boolean))];
      const exits   = [...new Set(rows.map(r => r["Exit Strategy"]).filter(Boolean))];

      if (!cancelled) {
        setEntryStrategies(entries);
        setExitStrategies(exits);
      }
    } catch (err) {
      console.error("Failed to prefill strategy options:", err);
    }
  })();

  return () => { cancelled = true; };
}, [userId, accountId]);


useEffect(() => {
  // If the user hasn't manually entered a value, set to suggested contracts
  if (
    calculatedSummary &&
    calculatedSummary.contracts !== undefined &&
    (journal["Size"] === undefined || journal["Size"] === "")
  ) {
    setJournal((prev) => ({
      ...prev,
      "Size": calculatedSummary.contracts
    }));
  }
  // eslint-disable-next-line
}, [calculatedSummary]);

// ---- Daily Goal Popup Effect ----
useEffect(() => {
  // Only check after settings are loaded
  if (!settings || !userId || !accountId) return;
  if (!popupsAllowedRef.current) return; // do not enqueue during settings edits
async function checkDailyGoalPopupOnLoad() {
  try {
    const freshSettings = await getSettings(userId, accountId);
    await checkDailyProfitTarget([], Number(freshSettings["Daily Goal"] || 0), freshSettings, updateSettings, userId, accountId, setSettings);
  } catch (err) {
    console.error("Failed to check daily goal popup on load:", err);
  }
}

  checkDailyGoalPopupOnLoad();
}, [settings, userId, accountId]); // Added currentPopup to dependencies


useEffect(() => {
  const initial = Number(settings["Initial Balance"] || 0);
  const goal = Number(settings["Account Goal"] || 0);
  const running = Number(settings["Running Balance"] || 0);

  let pct = 0;
  if (goal > initial) {
    pct = ((running - initial) / (goal - initial)) * 100;
  }
  pct = Math.max(0, Math.min(100, pct)); // Clamp between 0 and 100
  setProgress(pct);
}, [
  settings["Initial Balance"],
  settings["Account Goal"],
  settings["Running Balance"]
]);

const baseBalance = Number(settings["Running Balance"] ?? 0);
// ---- Save Trade Handler ----

// ---- Save Trade Handler ----
// ---- Save Trade Handler (robust: AB-or-Ticks required, computes AB if missing) ----
const handleSaveTrade = async () => {
  const errors = [];

  // 1) "Actual Balance" provided? (blank/undefined do NOT count)
  const abRaw = journal["Actual Balance"];
  const abProvided =
    !(abRaw === "" || abRaw == null) && Number.isFinite(Number(abRaw));

  // 2) Ticks from user input ("Ticks" new, "Tcks" legacy)
  let ticksRaw = journal.hasOwnProperty("Ticks")
    ? journal["Ticks"]
    : journal["Tcks"];
  let ticksProvided = !(ticksRaw === "" || ticksRaw == null);
  let ticksNum = Number(ticksRaw); // may be 0 if user typed 0 (that counts as provided)
  let hasTicks = ticksProvided && Number.isFinite(ticksNum);

const isDidNotTake =
  String(journal["Position"] || "").trim().toLowerCase() === "did not take";

// 2b) If ticks are NOT provided, try to derive them from Entry/Exit Price
//     ✅ Only when BOTH prices are filled in
if (!hasTicks && !isDidNotTake) {
  const absFromPrice = computeTicksFromPricesAbs(
    settings["Product"],
    journal["Entry Price"],
    journal["Exit Price"]
  );

  if (absFromPrice != null && Number.isFinite(absFromPrice)) {
    const isLoss = !!journal["Loss"];
    const signed = isLoss ? -absFromPrice : absFromPrice;

    ticksNum = signed;
    hasTicks = true;
  }
}


  // Now we can require one or the other
  //if (!isDidNotTake && !abProvided && !hasTicks) {
  //  errors.push("- Actual Balance or Ticks");
  //}


  // 3) Try to derive ticks from Entry/Exit price when Ticks field is empty
  const ticksFromPriceAbs = !hasTicks
    ? computeTicksFromPricesAbs(
        settings["Product"],
        journal["Entry Price"],
        journal["Exit Price"]
      )
    : null;

  const hasPriceDerivedTicks =
    ticksFromPriceAbs != null && Number.isFinite(ticksFromPriceAbs);

  // Require one of: Actual Balance, Ticks, or Entry/Exit Price (unless DNT)
  if (!isDidNotTake && !abProvided && !hasTicks && !hasPriceDerivedTicks) {
    errors.push("- Actual Balance or Ticks or Entry/Exit Price");
  }

  // Other required fields (keep these consistent with your current flow)
  if (!journal["Date"]) errors.push("- Date");
  if (!settings["Product"]) errors.push("- Product");
  //if (!journal["Did I follow my Rules?"]) errors.push("- Did I follow my Rules?");
   {
     const pos = (journal["Position"] || "").trim().toLowerCase();
     const requireWinLoss = pos !== "did not take";
     if (requireWinLoss && !journal["Win"] && !journal["Loss"]) {
       errors.push("- Win/Loss");
     }
   }

  // Contracts: prefer user-entered size; else suggested from calculatedSummary
  const effectiveContracts =
    (journal["Size"] !== undefined && journal["Size"] !== "")
      ? Number(journal["Size"])
      : Number(calculatedSummary?.contracts ?? 0);

  if (!Number.isFinite(effectiveContracts) || effectiveContracts <= 0) {
    errors.push("- # of Contracts");
  }

  // Enforce “calculate before save”
  if (!calculatedSummary) errors.push("- Click the Calculate button before saving");

  if (errors.length > 0) {
    setMissingFields(errors);
    return;
  }

  setSavingTrade(true);
  // Show only "Trade Saved" on save (other popups disabled per your request)
  setCurrentPopup({ id: "tradeSaved" });

  try {
    const tradeNum = await getNextTradeNumber();

    // Previous balance and price-per-tick
    const pricePerTick = getPricePerTick(settings["Product"]);
    const previousRB   = Number(settings["Running Balance"] || 0);

    // Use user-entered ticks if present, otherwise ticks deduced from Entry/Exit price
    const baseTicksAbs = hasTicks
      ? Math.abs(ticksNum)
      : (hasPriceDerivedTicks ? Math.abs(ticksFromPriceAbs) : 0);

    // Signed ticks: losses are negative, wins are positive (0 for "Did Not Take")
    const signedTicks =
      isDidNotTake || !Number.isFinite(baseTicksAbs)
        ? 0
        : (journal["Loss"] ? -baseTicksAbs : baseTicksAbs);

    const effectiveHasTicks =
      Number.isFinite(signedTicks) && baseTicksAbs >= 0;

    // If any ticks are available (typed or derived), compute PnL from ticks
    const computedPnl = effectiveHasTicks
      ? Number(effectiveContracts) * Number(pricePerTick) * Number(signedTicks)
      : 0;


    // If AB provided, use it; else compute from previousRB + computedPnl
    const actualBalanceNum = abProvided
      ? Number(abRaw)
      : +(previousRB + computedPnl).toFixed(2);

    // Common computed columns
    const actualRewardDollar = +(actualBalanceNum - previousRB).toFixed(2);
    const actualPctGrowth =
      previousRB > 0 ? (actualRewardDollar / previousRB) * 100 : 0;

    // Normalize times and duration (helpers already in this file)
    const entry24 = to24HourTime(journal["Entry Time"]);
    const exit24  = to24HourTime(journal["Exit Time"]);
    const durationHHMMSS = entry24 && exit24 ? getDurationHHMMSS(entry24, exit24) : "";
    const durationHMM    = hhmmssToHMM(durationHHMMSS);

    // Distance conversions for Risk/Reward and Total Risk calculations
    const slMode = settings["Stop Loss Mode"] || "Ticks";
    const tpMode = settings["Take Profit Mode"] || "Ticks";
    const rawSL = Number(settings["Stop Loss"] || 0);
    const rawTP = Number(settings["Take Profit"] || 0);
    
    const entryPriceForCalc = Number(journal["Entry Price"] || settings["Entry Price"] || 0);
    const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;

    let slDistance = rawSL;
    if (slMode === "Price" && entryPriceForCalc > 0) {
        slDistance = tickSize > 0 ? Math.abs(entryPriceForCalc - rawSL) / tickSize : 0;
    }

    let tpDistance = rawTP;
    if (tpMode === "Price" && entryPriceForCalc > 0) {
        tpDistance = tickSize > 0 ? Math.abs(rawTP - entryPriceForCalc) / tickSize : 0;
    }

    let rrStr = "";
    if (tpDistance > 0 && slDistance > 0) {
      const rr = tpDistance / slDistance;
      rrStr = `1:${Number.isInteger(rr) ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}`;
    }

    // Percent of Balance (risk dollars) shown in Summary
    const percentOfBalance = Number(settings["Running Balance"] || 0) *
                             (Number(settings["Risk Percentage"] || 0) / 100);

    // Build a row in your header order (keeps export alignment stable)
    const tradeObj = {};
    SHEET_HEADERS.forEach((header) => {
      switch (header) {
        case "Trade #":
          tradeObj[header] = tradeNum;
          break;
        case "tradeNum":
          tradeObj["tradeNum"] = tradeNum;
          break;
        case "Product":
          tradeObj[header] = settings["Product"];
          break;
        case "Running Balance":
        case "Actual Balance":
          tradeObj[header] = actualBalanceNum;
          break;
        case "Price Per Tick":
          tradeObj[header] = pricePerTick;
          break;
        case "Risk To Reward Ratio":
          tradeObj[header] = rrStr;
          break;
        case "Percent of Balance":
          tradeObj[header] = percentOfBalance;
          break;
        case "Stop Loss_Take Profit":
          tradeObj[header] = (rawSL && rawTP) ? `${rawSL}/${rawTP}` : "";
          break;
        case "Suggested Max # of Contracts":
          tradeObj[header] = calculatedSummary?.contracts ?? "";
          break;
        case "Max Stop Loss Size for 1 Contract(s) (Ticks)":
          tradeObj[header] =
            pricePerTick > 0 ? Math.floor(percentOfBalance / pricePerTick) : 0;
          break;
        case "Total Risk":
          tradeObj[header] = Number(effectiveContracts) * slDistance * pricePerTick;
          break;
        case "Size":
          tradeObj[header] =
            (journal["Size"] !== undefined && journal["Size"] !== "")
              ? Number(journal["Size"])
              : Number(calculatedSummary?.contracts ?? 0);
          break;
        case "Tcks":
        case "Ticks": {
          const toStore = effectiveHasTicks ? signedTicks : "";
          tradeObj[header] = toStore;
          break;
        }
        case "Actual Reward":
          tradeObj[header] = actualRewardDollar;
          break;
        case "Actual Percent Growth":
          tradeObj[header] = `${actualPctGrowth.toFixed(2)}%`;
          break;
        case "Actual Risk to Reward": {
          const isDidNotTake =
            String(journal["Position"] || "").trim().toLowerCase() === "did not take";

          const actualReward = Number(tradeObj["Actual Reward"] || 0);
          const totalRisk = Number(tradeObj["Total Risk"] || 0);
          let rrValue = "";

          // Leave blank if position was Did Not Take
          if (isDidNotTake) {
            rrValue = "";
          } else if (totalRisk > 0) {
            const rr = actualReward / totalRisk;
            rrValue = `${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}:1`;
          }

          tradeObj[header] = rrValue;
          break;
        }
        case "Entry Time":
          tradeObj[header] = entry24 || journal["Entry Time"] || "";
          break;
        case "Exit Time":
          tradeObj[header] = exit24 || journal["Exit Time"] || "";
          break;
        case "Entry Price":
          tradeObj[header] =  journal["Entry Price"] || "";
          break;
        case "Exit Price":
          tradeObj[header] = journal["Exit Price"] || "";
          break;
        case "Potential":
          tradeObj[header] = journal["Potential"] || "";
          break;
        case "Ideal Stop Loss":
          tradeObj[header] = journal["IdealSL"] || "";
          break;
        case "Duration (h:mm)":
          // Write h:mm (e.g., "1:05")
          tradeObj[header] = durationHMM;
          break;
         case "Timeframe":
          tradeObj["Timeframe"] = journal["Timeframe"] || tradeObj["Timeframe"] || "";
                  break;        default:
          // Copy over any field from journal if present; else blank.
          tradeObj[header] = (journal[header] ?? "");
      }
    });

    // Ensure Win/Loss stay boolean (some sheets expect boolean strings)
    if (typeof tradeObj["Win"] === "string")
      tradeObj["Win"] = tradeObj["Win"].toLowerCase() === "true";
    if (typeof tradeObj["Loss"] === "string")
      tradeObj["Loss"] = tradeObj["Loss"].toLowerCase() === "true";
    tradeObj["Timeframe"] =
      (tradeObj["Timeframe"] ??
       journal["Timeframe"] ??
       journal["Time Frame"] ??
       "");
    tradeObj["Potential"] = journal["Potential"] || "";
    // Background calculation for Futures
          // (tickSize is already calculated correctly above, so we just use it here)
          tradeObj["tickSize"] = tickSize;
          tradeObj["assetClass"] = "FUTURES";
          
          // --- NEW: EXPLICIT BALANCE SNAPSHOTS FOR THE JOURNAL ---
          tradeObj["Buying Power"] = actualBalanceNum;
          tradeObj["Unsettled Balance"] = 0;
          tradeObj["Total Equity"] = actualBalanceNum;
          tradeObj["Needs Review"] = !!journal["Needs Review"];
          tradeObj["Emotional Notes"] = journal["Emotional Notes"] || "";
          tradeObj["Target Reached"] = !!journal["Target Reached"];
    // Persist trade
    await appendTrade(userId, accountId, tradeObj);

    // Update running balance and refresh summary
    await updateSettings(userId, accountId, { 
      "Running Balance": actualBalanceNum,
      "Beginning Balance": actualBalanceNum 
    });
    
    setSettings(prev => ({ 
      ...prev, 
      "Running Balance": actualBalanceNum,
      "Beginning Balance": actualBalanceNum 
    }));
    
    if (typeof runSummaryOnly === "function") {
      runSummaryOnly({ "Running Balance": actualBalanceNum, "Beginning Balance": actualBalanceNum });
    }

    // Optional: show AccountGoalReached (allowed to remain)
    const goal = Number(settings["Account Goal"] || 0);
    if (goal > 0 && actualBalanceNum >= goal) {
      queuePopup?.("accountGoalReached", { balance: actualBalanceNum, goal });
    }

    // Reset the form (keep suggested contracts if available)
    setJournal(prev => ({
      ...defaultJournal,
      Date: prev?.Date || getTodayLocalISO?.() || "",
      Size: calculatedSummary?.contracts ?? "",
      Timeframe: String(settings?.["Default Timeframe"] || "").trim(),
      Session: String(settings?.["Default Session"] || "").trim(),
      "Entry Strategy": String(settings?.["Default Entry Strategy"] || "").trim(),
      "Exit Strategy": String(settings?.["Default Exit Strategy"] || "").trim(),
    }));
  } catch (err) {
    alert("Failed to save journal entry.");
    console.error("handleSaveTrade error:", err);
  } finally {
    setSavingTrade(false);
  }
};



// End Save Trade
const isDidNotTake =
  (journal["Position"] || "").trim().toLowerCase() === "did not take";

useBlockWheelOnNumber(actualBalRef);
return (
  <div className="space-y-8 max-w-5xl mx-auto py-10">
    {/* --- Account Settings Card --- */}
    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 mb-4 animate-fadeIn">
      <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
  {/* Wrap the Icon and Title in their own flex container */}
  <div className="flex items-center gap-2">
    <IconGoal />
    <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">Trade Setup (Backtesting)</h2>
  </div>

  {/* NEW STRATEGY CHECKLIST BUTTON */}
  <button 
    onClick={() => setShowStrategyModal(true)}
    className="ml-auto md:ml-0 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 font-bold px-4 py-2 rounded-lg transition"
  >
    <FaClipboardList /> Strategy Checklist
  </button>
</div>

      <div className="space-y-6">
        
        {/* --- COLLAPSIBLE BALANCES SECTION --- */}
        <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span>Account Cash Status</span>
              <span className="text-sm font-normal text-gray-500">
                (Running Balance: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"])}</span>)
              </span>
            </div>
            <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
            <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
          </summary>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
              <input 
                type="text" 
                inputMode="decimal" 
                value={settings["Account Goal"] ?? ""} 
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9.-]/g, "");
                  handleSettingChange("Account Goal", raw);
                }}
                onBlur={(e) => {
                  const n = parseFloat(e.target.value);
                  if (!isNaN(n)) {
                    const formatted = n.toFixed(2);
                    handleSettingChange("Account Goal", formatted);
                    // Instantly recalculate projections with the new Account Goal
                    if (typeof runSummaryOnly === "function") {
                      runSummaryOnly({ "Account Goal": Number(formatted) });
                    }
                  }
                }}
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400 bg-white" 
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Running Balance</label>
              <input 
                type="text" 
                inputMode="decimal" 
                value={settings["Running Balance"] ?? ""} 
                onChange={(e) => {
                  // Allow user to freely type numbers, decimals, and minus signs
                  const raw = e.target.value.replace(/[^0-9.-]/g, "");
                  setSettings((s) => ({ ...s, "Running Balance": raw }));
                }}
                onBlur={(e) => {
                  const n = parseFloat(e.target.value);
                  if (!isNaN(n)) {
                    const formatted = n.toFixed(2);
                    // 1. Update the state with the formatted value
                    setSettings((s) => ({ ...s, "Running Balance": formatted }));
                    
                    // 2. Auto-run the background calculation with the new value
                    if (typeof runSummaryOnly === "function") {
                      runSummaryOnly({ "Running Balance": Number(formatted) });
                    }
                  }
                }}
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400 bg-white font-bold" 
                placeholder="0.00"
              />
            </div>
          </div>
        </details>

        {/* --- STEP 1: THE SETUP --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
                Product<span className="text-red-500 text-lg">*</span>
              </label>
              <select 
                value={settings["Product"] || ""} 
                onChange={e => handleSettingChange("Product", e.target.value)} 
                className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white bg-gray-50 focus:ring-2 focus:ring-blue-400 transition ${
                  missingFields.includes("Product") ? "border-red-500" : "border-gray-200 dark:border-gray-600"
                }`}
              >
                <option value="">Select Product</option>
                {favoriteFuturesOptions.length > 0 && (
                  <optgroup label="♥ Favorites">
                    {favoriteFuturesOptions.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="All Futures">
                  {nonFavoriteOptions.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            
            {/* Added Entry Price to allow calculating Ticks from Price */}
            <div>
              <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Entry Price (Optional)</label>
              <input 
                type="number" 
                placeholder="e.g. 4500.25" 
                value={settings["Entry Price"]||""} 
                onChange={e=>handleSettingChange("Entry Price",e.target.value)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400" 
              />
            </div>
          </div>
        </div>

        {/* --- STEP 2: EXITS --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
          {/* Changed grid-cols-2 to grid-cols-1 to stack them on separate lines */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Stop Loss Group */}
            <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-1">
                  <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
                  <Tooltip 
                    variant="blue" 
                    backdrop 
                    content={
                      <div className="space-y-3 p-1">
                        <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                          Tip
                        </div>
                        <p className="text-[15px] leading-relaxed text-gray-100">
                          In fast markets, you don’t need an exact stop loss price. Use a reasonable estimate based on your strategy (example: <span className="font-bold text-white">10–20 ticks</span> on ES, <span className="font-bold text-white">20–40 ticks</span> on NQ).
                        </p>
                        <p className="text-[15px] font-bold text-blue-300">
                          Backtesting your strategy is an excellent way to determine that number.
                        </p>
                      </div>
                    }
                  >
                    <span className="cursor-help text-[10px] text-blue-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                  </Tooltip>
                </div>
                <select 
                  value={settings["Stop Loss Mode"] || "Ticks"} 
                  onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                  className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="Ticks">by Ticks</option>
                  <option value="Price">by Price</option>
                </select>
              </div>
              <input 
                type="number" 
                placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : "Ticks"}
                value={settings["Stop Loss"] || ""} 
                onChange={e => handleSettingChange("Stop Loss", e.target.value)} 
                className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400 bg-white"
              />
              {/* Dynamic Helper Text */}
              {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
                <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                  {settings["Stop Loss Mode"] === "Price" 
                    ? `Est. Ticks: ${Math.round(Math.abs(settings["Entry Price"] - settings["Stop Loss"]) / (getTickSizeForProduct(settings["Product"]) || 0.25))}`
                    : `Est. Stop Price: ${Number(settings["Entry Price"]) - (Number(settings["Stop Loss"]) * (getTickSizeForProduct(settings["Product"]) || 0.25))}`}
                </p>
              )}
            </div>

            {/* Take Profit Group */}
            <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
              <div className="flex justify-between items-center mb-2">
                <label className="font-bold text-gray-800 dark:text-gray-200">Take Profit</label>
                <select 
                  value={settings["Take Profit Mode"] || "Ticks"} 
                  onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                  className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
                >
                  <option value="Ticks">by Ticks</option>
                  <option value="Price">by Price</option>
                </select>
              </div>
              <input 
                type="number" 
                placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : "Ticks"}
                value={settings["Take Profit"] || ""} 
                onChange={e => handleSettingChange("Take Profit", e.target.value)} 
                className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400 bg-white"
              />
              {/* Dynamic Helper Text */}
              {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
                <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                  {settings["Take Profit Mode"] === "Price" 
                    ? `Est. Ticks: ${Math.round(Math.abs(settings["Take Profit"] - settings["Entry Price"]) / (getTickSizeForProduct(settings["Product"]) || 0.25))}`
                    : `Est. Target Price: ${Number(settings["Entry Price"]) + (Number(settings["Take Profit"]) * (getTickSizeForProduct(settings["Product"]) || 0.25))}`}
                </p>
              )}
            </div>

          </div>
        </div>

        {/* --- STEP 3: RISK PARAMETERS --- */}
        <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Risk Parameters</h3>
          <div>
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget<span className="text-red-500 text-lg">*</span></label>
              </div>
              {/* Converted to matching Dropdown */}
              <select 
                value={settings["Risk Mode"] || "%"} 
                onChange={e => handleSettingChange("Risk Mode", e.target.value)} 
                className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="$">$ Amount</option>
                <option value="%">% of Balance</option>
              </select>
            </div>
            
            {(settings["Risk Mode"] || "%") === "%" ? (
              <input
                type="text"
                inputMode="decimal"
                value={settings["Risk Percentage"] ?? ""}
                onChange={e => handleSettingChange("Risk Percentage", e.target.value)}
                onBlur={e => handleSettingChange("Risk Percentage", commitNumberOrEmpty(e.target.value))}
                className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400 bg-white"
                placeholder="e.g. 1.5"
              />
            ) : (
              <input
                type="text"
                inputMode="numeric"
                value={settings["Risk Amount"] ?? ""}
                onChange={e => handleSettingChange("Risk Amount", e.target.value)}
                onBlur={e => handleSettingChange("Risk Amount", commitNumberOrEmpty(e.target.value))}
                className="no-spin w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400 bg-white"
                placeholder="e.g. 150"
              />
            )}
          </div>
        </div>

        {/* --- PROJECTIONS ROW --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
            <input 
              type="number" 
              value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} 
              readOnly 
              disabled 
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" 
            />
          </div>
          <div className="flex items-end">
            <button 
              type="button" 
              className={`w-full h-[42px] bg-blue-600 dark:bg-blue-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-blue-700 hover:dark:bg-blue-800 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`} 
              onClick={() => setProjectionOpen(true)} 
              disabled={!calculatedSummary}
            >
              Win Projections
            </button>
          </div>
          <div className="flex items-end">
            <button 
              type="button" 
              className={`w-full h-[42px] bg-red-600 dark:bg-red-700 text-white rounded-xl px-3 py-2 font-semibold hover:bg-red-700 hover:dark:bg-red-800 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`} 
              onClick={() => setLossProjectionOpen(true)} 
              disabled={!calculatedSummary}
            >
              Loss Projections
            </button>
          </div>
        </div>
      </div>




 {/* Divider */}
<div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />



{/* Daily Goal & Daily Loss amounts — place directly under Row 4 */}
<div className="col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
  {/* Daily Goal (amounts) */}
  <div hidden>
    <div className="flex items-center justify-between mb-1">
      <span className="font-bold text-gray-800 dark:text-gray-100">Daily Goal</span>
      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
        {formatCurrency(dailyGoalProg.done)} / {formatCurrency(dailyGoalProg.target)}
      </span>
    </div>
    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
      <div
        className="h-3 bg-green-500 transition-all duration-500"
        style={{ width: `${dailyGoalProg.pct}%` }}
      />
    </div>
  </div>

  {/* Daily Loss Limit (amounts) */}
  <div hidden>
    <div className="flex items-center justify-between mb-1">
      <span className="font-bold text-gray-800 dark:text-gray-100">Daily Loss Limit</span>
      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
        {formatCurrency(dailyLossProg.lost)} / {formatCurrency(dailyLossProg.limit)}
      </span>
    </div>
    <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
      <div
        className="h-3 bg-red-500 transition-all duration-500"
        style={{ width: `${dailyLossProg.pct}%` }}
      />
    </div>
  </div>
</div>




      {/* Progress Bar */}
      <div className="mt-2 mb-2">
        <div className="flex items-center gap-4">
          <span className="font-bold text-gray-800 dark:text-gray-100">Goal Progress</span>
          <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-3 rounded-lg relative overflow-hidden">
            <div
              className="bg-green-500 h-3 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{Math.round(progress)}%</span>
        </div>
      </div>

  <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

      {/* Summary Section */}
      <div className="max-w-lg mx-auto border rounded-xl bg-gray-50 dark:bg-gray-800 p-4 mt-4">
        <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white"><IconGoal />Summary</h3>
        {calculatedSummary ? (
          <>
            <SummaryRow label="Price Per Tick" value={calculatedSummary.pricePerTick ? formatCurrency(calculatedSummary.pricePerTick) : ""} />
            <SummaryRow label="Reward to Risk Ratio" value={calculatedSummary.riskRewardRatio} />
             {String(settings["Risk Mode"] || "Percent") === "Dollar" ? (
               <>
                 <SummaryRow
                   label="Risk Amount (Fixed $)"
                   value={calculatedSummary.percentOfBalance ? formatCurrency(calculatedSummary.percentOfBalance) : ""}
                 />
                 <SummaryRow
                   label="Risk % of Balance"
                   value={`${(calculatedSummary.riskPctFromBase ?? 0).toFixed(2)}%`}
                 />
               </>
             ) : (
               <>
                 <SummaryRow
                   label="Risk % of Balance"
                   value={`${Number(settings["Risk Percentage"] || 0).toFixed(2)}%`}
                 />
                 <SummaryRow
                   label="Risk Budget ($)"
                   value={calculatedSummary.percentOfBalance ? formatCurrency(calculatedSummary.percentOfBalance) : ""}
                 />
               </>
             )}
            <SummaryRow label="Stop Loss" value={calculatedSummary.stopLoss && calculatedSummary.takeProfit ? `${calculatedSummary.stopLoss}` : ""} />
            <SummaryRow label="Take Profit" value={calculatedSummary.stopLoss && calculatedSummary.takeProfit ? `${calculatedSummary.takeProfit}` : ""} />

            <SummaryRow
              label={
                <span className="inline-flex items-center gap-2">
                  Total Risk
                  {calculatedSummary && (
                    <Tooltip
                      variant={
                        // Neutral panel when suggested = 0; otherwise blue on pass, red on fail
                        Number(calculatedSummary.contracts || 0) === 0
                          ? "neutral"
                          : (Number(calculatedSummary.totalRisk || 0) <= Number(calculatedSummary.percentOfBalance || 0))
                              ? "blue"
                              : "red"
                      }
                      backdrop
                      content={(() => {
                        const pricePerTick = Number(calculatedSummary.pricePerTick || 0);
                        const stopLoss = Number(calculatedSummary.stopLoss || 0);
                        const suggested = Number(calculatedSummary.contracts || 0);
                        const minQty = Math.max(1, Number(calculatedSummary.minContracts || 1));
                        const usedContracts = suggested > 0 ? suggested : minQty;

                        const riskUsed = pricePerTick * stopLoss * usedContracts;
                         // dollar budget and the display percent (≈ in $ mode)
                          const budgetDollars = Number(calculatedSummary.percentOfBalance || 0);
                          const approxPct = Number(
                            (calculatedSummary.riskPctFromBase ?? calculatedSummary.riskPercent ?? 0)
                          ).toFixed(2);

                        const isZero = suggested === 0;

                        return (
                          // Make the CONTENT red when suggested = 0 (panel stays neutral)
                          <div className={`space-y-2 ${isZero ? "text-red-600 dark:text-red-400" : ""}`}>
                            <div
                              className={
                                "text-sm uppercase tracking-wide font-bold opacity-100 " +
                                (isZero ? "text-red-700 dark:text-red-500" : "text-blue-700 dark:text-blue-400")
                              }
                            >
                              How we calculate total risk
                            </div>

                            {/* Plain-English formula */}
                            <div className="font-sans text-[16px]">
                              Price Per Tick × Stop Loss × Suggested Contracts
                            </div>

                            {/* Formula with actual values USED for Total Risk */}
                            <div className="font-sans text-[16px]">
                              {formatCurrency(pricePerTick)} × {stopLoss} ticks × {usedContracts} contract{usedContracts === 1 ? "" : "s"}
                              {isZero && <span className="opacity-80"> (min)</span>}{" "}
                              = <strong>{formatCurrency(riskUsed)}</strong>
                            </div>

                            {/* If suggested is 0, explain the fallback */}
                            {isZero && (
                              <p className="text-[13px]">
                                Suggested contracts is <span className="font-semibold">0</span>, so we use your minimum:{" "}
                                <span className="font-semibold">{minQty}</span>.
                              </p>
                            )}

                            {/* Percent of balance to compare against */}
                            <div className="font-sans text-[16px]">
                              Percent of balance (≈ {approxPct}%): <strong>{formatCurrency(budgetDollars)}</strong>
                            </div>

                            <div className={`mt-1 text-[16px] font-semibold ${
                              riskUsed <= budgetDollars ? "text-green-300" : "text-red-400 dark:text-red-400"
                            }`}>
                              {riskUsed <= budgetDollars ? "✓" : "✕"} Total risk {riskUsed <= budgetDollars ? "is" : "is not"} ≤ percent of balance
                            </div>


                            {/* Hide this advice when suggested = 0 */}
                            {suggested > 0 && (
                              <div className="font-sans text-[14px] mb-6">
                                If you want to trade with more contracts you can increase the risk percentage to an amount you are comfortable with.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    >
                      {/* Single trigger element (child of Tooltip). No second Tooltip needed. */}
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold"
                        aria-label="Show how total risk is calculated"
                      >
                        ?
                      </span>
                    </Tooltip>
                  )}
                </span>
              }
              value={calculatedSummary.totalRisk ? formatCurrency(calculatedSummary.totalRisk) : ""}
              red={calculatedSummary.contracts === 0}
            />
            <SummaryRow label="Target Reward" value={calculatedSummary.targetReward ? formatCurrency(calculatedSummary.targetReward) : ""} />
            <SummaryRow
              label={
                <span className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300">
                  {`Max Stop Loss Size for ${calculatedSummary.minContracts} contract(s)`}

                  <Tooltip
                    variant="blue"
                    backdrop
                    content={(() => {
                      const minQty = Math.max(1, Number(calculatedSummary.minContracts || 1));
                      const ppt = Number(calculatedSummary.pricePerTick || 0);           // $ per tick
                      const pob = Number(calculatedSummary.percentOfBalance || 0);       // $ risk budget
                      const maxTicksForMin = (ppt > 0 && minQty > 0)
                        ? Math.floor(pob / (ppt * minQty))
                        : 0;

                      return (
                        <div className="space-y-2">
                          <div className="text-sm uppercase font-bold opacity-100 text-blue-700 dark:text-blue-400">
                            Max stop loss for minimum contracts
                          </div>

                          <p className="text-[16px] ">
                            Your <strong>minimum contract</strong> setting is used to calculate
                            the largest stop loss (in ticks) you can take while staying within your risk tolerance.
                          </p>



                          {/* Guidance */}
                          <p className="text-[16px] leading-relaxed">
                            If the maximum stop loss is a <strong>very large number</strong>,
                            that can indicate you should increase the <strong>minimum contracts </strong>
                            in your settings.
                          </p>
                        </div>
                      );
                    })()}
                  >
                    {/* Small “?” trigger button; fully accessible */}
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full
                                 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200
                                 text-[12px] font-bold cursor-help"
                      aria-label="Explain how max stop loss size for minimum contracts is calculated"
                    >
                      ?
                    </span>
                  </Tooltip>
                </span>
              }
              value={
                Number.isFinite(calculatedSummary.maxStopLossSizeForMin)
                  ? <span className="text-gray-600 dark:text-gray-300 font-semibold">
                      {calculatedSummary.maxStopLossSizeForMin}
                    </span>
                  : ""
              }
            />
            <div
              className={
                calculatedSummary.contracts === 0
                  ? "rounded-md px-2 py-1 bg-red-50 dark:bg-red-900/40"
                  : "rounded-md px-2 py-1 bg-blue-300 dark:bg-blue-400/30"
              }
            >
              <SummaryRow
                label={<span className="text-gray-600 dark:text-gray-300">Suggested # of Contracts</span>}
                value={
                  calculatedSummary.contracts === 0
                    ? <span className="text-red-600 font-bold">0</span>
                    : <span className="text-gray-600 dark:text-gray-300 font-semibold">
                        {calculatedSummary.contracts}
                      </span>
                }
                red={calculatedSummary.contracts === 0}
              />
            </div>
                      </>
        ) : (
          <div className="text-gray-400 dark:text-gray-600 text-center py-6">No calculations yet. Fill out the form above and click Calculate.</div>
        )}
      </div>
      {/* Save Settings */}
      <div className="flex gap-4 justify-end mt-8">
        <button
          type="button"
          onClick={() => handleCalculate(true)}
          disabled={savingSettings || !settings["Product"]}
          aria-disabled={savingSettings || !settings["Product"]}
          title={!settings["Product"] ? "Select a product first" : undefined}
          className="bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-800 dark:to-blue-500 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-500 dark:hover:from-blue-900 dark:hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {savingSettings ? "Calculating..." : "Calculate"}
        </button>

      </div>
    </div>



{/* Journal Entry - Logical Grouping Layout (Futures Backtest) */}
    {!settings["Calculator Only"] && (
<div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
  
  {/* Header */}
  <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
    <IconJournal />
    <div>
      <h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Log your backtest results below. <span className="text-red-500">* denotes required</span>
      </p>
    </div>
  </div>

  <div className="space-y-8"> 

{/* SECTION 1: SETUP & CONTEXT */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">
        1. Setup & Context
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Date */}
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500 text-lg">*</span></label>
          <input 
            type="date" 
            value={journal["Date"] || getTodayLocalISO()} 
            onChange={e => handleJournalChange("Date", e.target.value)} 
            className={`w-full h-[42px] px-4 py-2 rounded-lg border bg-gray-50 dark:bg-gray-700 dark:text-white transition focus:ring-2 focus:ring-blue-400 ${
              missingFields.includes("Date") ? "border-red-500" : "border-gray-200 dark:border-gray-700"
            }`}
          />
        </div>
        
        {/* Session */}
        {!settings["Hide Session"] && (
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label>
          <select 
            id="session"
            value={journal["Session"]} 
            onChange={e => handleJournalChange("Session", e.target.value)} 
            className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
          >
            <option value="">Select</option>
            {SESSION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        )}

        {/* Timeframe */}
        <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label>
           <input 
             id="timeframe"
             type="text" 
             list="timeframe-options" 
             value={journal["Timeframe"]} 
             onChange={e => handleJournalChange("Timeframe", e.target.value)} 
             placeholder="e.g. 5m"
             className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition" 
           />
           <datalist id="timeframe-options">
              {TIMEFRAME_OPTIONS.map(tf => <option key={tf} value={tf} />)}
           </datalist>
        </div>

        {/* Position */}
        <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label>
           <select 
             id="position"
             value={journal["Position"]} 
             onChange={e => handleJournalChange("Position", e.target.value)} 
             className="w-full h-[42px] px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
           >
            <option value="">Select</option>
            {POSITION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </div>
    </div>

    {/* SECTION 2: EXECUTION */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">
        2. Execution
      </h4>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Entry Group */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label>
          <div className="space-y-4">
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
               <div className="relative flex items-center">
                  <input 
                    id="entryTime" 
                    type="time" 
                    ref={entryTimeRef} 
                    value={to24HourTime(journal["Entry Time"])} 
                    onChange={e => {
                      const rawTime24 = e.target.value;
                      setJournal(prev => {
                        const nextState = { ...prev, "Entry Time": toAmPm(rawTime24) };
                        
                        // Auto-populate Session only if it is currently empty
                        if (!prev.Session || prev.Session === "") {
                          const calculatedSession = getSessionFromTime(rawTime24);
                          if (calculatedSession) {
                            nextState.Session = calculatedSession;
                          }
                        }
                        
                        return nextState;
                      });
                    }} 
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" 
                  />
                  <FaRegClock
                    className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                    onClick={() => entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()}
                  />
               </div>
             </div>

             {!settings["Hide Entry Price"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
               <input
                  id="entryPrice"
                  type="number"
                  inputMode="decimal"
                  value={journal["Entry Price"] ?? ""}
                  onChange={e => handleJournalChange("Entry Price", e.target.value)}
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  placeholder="e.g. 4523.25"
                />
             </div>
             )}

             {!settings["Hide Entry Strategy"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
               <input
                  id="entryStrategy"
                  type="text"
                  value={journal["Entry Strategy"]}
                  onChange={e => handleJournalChange("Entry Strategy", e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  list="entry-strategy-options"
                />
                <datalist id="entry-strategy-options">
                  {entryStrategies.map(strategy => <option key={strategy} value={strategy} />)}
                </datalist>
             </div>
             )}
          </div>
        </div>

        {/* Exit Group */}
        <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
          <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label>
          <div className="space-y-4">
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
               <div className="relative flex items-center">
                  <input
                    id="exitTime"
                    type="time"
                    ref={exitTimeRef}
                    className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                    value={to24HourTime(journal["Exit Time"])}
                    onChange={e => setJournal(prev => ({ ...prev, "Exit Time": e.target.value }))}
                  />
                  <FaRegClock
                    className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500"
                    onClick={() => exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()}
                  />
               </div>
             </div>

             {!settings["Hide Exit Price"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
               <input
                  id="exitPrice"
                  type="number"
                  inputMode="decimal"
                  value={journal["Exit Price"] ?? ""}
                  onChange={e => handleJournalChange("Exit Price", e.target.value)}
                  className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition"
                  placeholder="e.g. 4527.50"
                />
             </div>
             )}

             {!settings["Hide Exit Strategy"] && (
             <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
               <input
                  id="exitStrategy"
                  type="text"
                  value={journal["Exit Strategy"]}
                  onChange={e => handleJournalChange("Exit Strategy", e.target.value)}
                  className="
                    w-full rounded-lg px-3 py-2
                    bg-gray-700 dark:bg-gray-700
                    text-gray-100
                    border border-gray-600
                    focus:bg-gray-700 focus:dark:bg-gray-700
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                  "
                  list="exit-strategy-options"
                />
                <datalist id="exit-strategy-options">
                  {exitStrategies.map(strategy => <option key={strategy} value={strategy} />)}
                </datalist>
             </div>
             )}
          </div>
        </div>

        {/* Calculated Stats Group */}
        <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                    <div className="space-y-6">
                      <div>
                        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
                          Actual Contract Size<span className="text-red-500 text-lg">*</span>
                        </label>
                        <input
                          id="contracts"
                          type="number"
                          step="1"
                          value={
                            journal["Size"] !== undefined && journal["Size"] !== ""
                              ? journal["Size"]
                              : calculatedSummary?.lots ?? ""
                          }
                          onChange={e => handleJournalChange("Size", e.target.value)}
                          className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                          placeholder="0.00"
                        />
                      </div>
                      
                      {/* NEW: Potential Ticks Field (Note Removed) */}
                      {!settings["Hide Potential"] && (
                      <div>
                        <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                          Potential Ticks
                          <Tooltip
                            variant="blue"
                            backdrop
                            content={
                              <div className="space-y-2">
                                <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                                  Potential Ticks
                                </div>
                                <p className="text-[14px] leading-relaxed">
                                  The total number of ticks that were available in the move.
                                </p>
                              </div>
                            }
                          >
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                              ?
                            </span>
                          </Tooltip>
                        </label>
                        <input
                          id="potentialTicks"
                          type="number"
                          value={journal["Potential"] ?? ""}
                          onChange={(e) => handleJournalChange("Potential", e.target.value)}
                          className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                          placeholder="Qty"
                        />
                      </div>
                      )}
                    </div>


            {!settings["Hide Ideal Stop Loss"] && (
            <div>
              <label className="block font-bold mb-1 mt-4 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                Ideal Stop Loss
                <Tooltip
                  variant="blue"
                  backdrop
                  content={
                    <div className="space-y-2">
                      <div className="text-sm uppercase tracking-wide font-bold opacity-100 text-blue-700 dark:text-blue-400">
                        Ideal Stop Loss
                      </div>
                      <p className="text-[14px] leading-relaxed">
                        You should always use your strategy for setting the proper stop loss. You may notice that the stop loss is too big or too small.
                      </p>
                      <p className="text-[14px] leading-relaxed">
                        You can use this field to help track a more ideal stop loss for the trade and see the results on the Stats page.
                      </p>
                    </div>
                  }
                >
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[12px] font-bold cursor-help">
                    ?
                  </span>
                </Tooltip>
              </label>
              <input
                id="ideal"
                type="number"
                value={journal["IdealSL"] ?? ""}
                onChange={e => handleJournalChange("IdealSL", e.target.value)}
                className="no-spin w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-white transition font-mono"
                placeholder="Qty"
              />
            </div>
            )}
         </div>

      </div>
    </div>

    {/* SECTION 3: OUTCOME */}
    <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold mb-4 border-l-4 border-green-500 pl-3">
        3. Outcome
      </h4>

      {/* 3a. Win/Loss Buttons */}
      <div className="mb-6">
        <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
          Result{!isDidNotTake && <span className="text-red-500 text-lg">*</span>}
        </label>
        <div className="flex gap-4 max-w-md">
          <button
            type="button"
            onClick={() => setJournal(j => ({ ...j, Win: true, Loss: false }))}
            disabled={isDidNotTake}
            className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
              journal["Win"] 
                ? "bg-green-500 text-white border-green-600 ring-2 ring-green-300 dark:ring-green-800" 
                : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-gray-600 disabled:opacity-50"
            }`}
          >
            WON
          </button>
          <button
            type="button"
            onClick={() => setJournal(j => ({ ...j, Win: false, Loss: true }))}
            disabled={isDidNotTake}
            className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${
              journal["Loss"] 
                ? "bg-red-500 text-white border-red-600 ring-2 ring-red-300 dark:ring-red-900" 
                : "bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-gray-600 disabled:opacity-50"
            }`}
          >
            LOSS
          </button>
        </div>
      </div>

      {/* 3b. Backtesting Instructions */}
      <div className="border border-yellow-300 dark:border-yellow-600 rounded-xl p-4 mb-6 bg-yellow-50 dark:bg-yellow-900/10">
        <div className="text-yellow-800 dark:text-yellow-200 mb-2 text-sm font-semibold flex items-start gap-2">
          <span className="mt-1">💡</span>
          <div>
            Enter <strong>New Balance</strong> OR <strong># of Ticks</strong>.
            <p className="font-normal mt-1 opacity-90">
              Entering Ticks will auto-calculate the New Balance based on your lot size. 
              If you enter a New Balance, the Ticks field will clear.
            </p>
          </div>
        </div>
      </div>

      {/* 3c. Balance / Ticks Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
        
        {/* New Balance */}
        <div>
           <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">New Account Balance</label>
           <div className="relative">
             <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
             <input
                id="actual-balance-input"
                type="text"
                inputMode="decimal"
                value={journal["Actual Balance"] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Clear Ticks when Actual Balance is entered
                  setJournal((j) => ({
                    ...j,
                    ["Actual Balance"]: raw,
                    ["Ticks"]: raw ? "" : j["Ticks"],
                  }));
                }}
                 onBlur={(e) => {
                   const raw = String(e.target.value ?? "").trim();
                   setJournal((j) => ({
                     ...j,
                     // keep empty when blank OR when Ticks is populated
                     ["Actual Balance"]: (raw === "" || String(j["Ticks"] ?? "").trim() !== "")
                       ? ""
                       : parseCurrency(raw),
                   }));
                 }}
                className={`w-full pl-8 px-4 py-3 rounded-xl border focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
                  missingFields.includes("- Actual Balance or Ticks")
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-500"
                }`}
                placeholder="0.00"
              />
           </div>
        </div>

        {/* Ticks */}
        <div>
          <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">
             Ticks Captured/Lost
          </label>
          <input
            id="ticks"
            type="number"
            value={journal["Ticks"] || ""}
            onChange={(e) => {
              const val = e.target.value;
              // Clear Actual Balance when Ticks are entered
              setJournal((j) => ({
                ...j,
                ["Ticks"]: val,
                ["Actual Balance"]: val ? "" : j["Actual Balance"],
              }));
            }}
            className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-yellow-400 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner ${
              missingFields.includes("- Actual Balance or Ticks")
                ? "border-red-500"
                : "border-gray-300 dark:border-gray-500"
            }`}
            placeholder="Qty"
            inputMode="numeric"
          />
        </div>

        {/* NEW: Target Reached Checkbox */}
              <div className="mt-6 flex items-center bg-green-50 dark:bg-green-900/10 p-3 rounded-xl border border-green-100 dark:border-green-800">
                 <input 
                     type="checkbox" 
                     id="targetReachedFut" 
                     checked={!!journal["Target Reached"]} 
                     onChange={e => handleJournalChange("Target Reached", e.target.checked)} 
                     className="mr-3 w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer" 
                 />
                 <label htmlFor="targetReachedFut" className="text-sm font-bold text-green-700 dark:text-green-400 cursor-pointer">
                     Target Reached?
                 </label>
              </div>

      </div>
    </div>

    {/* SECTION 4: PSYCHOLOGY & REVIEW */}
    <div>
      <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">
        4. Psychology & Review
      </h4>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        {/* Rules */}
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?</label>
            <select
              id="rules"
              value={journal["Did I follow my Rules?"]}
              onChange={e => handleJournalChange("Did I follow my Rules?", e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select</option>
              {RULES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        {/* Mood */}
        {!settings["Hide Mood"] && (
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label>
            <select
              id="mood"
              value={journal["Mood_Mindset"]}
              onChange={e => handleJournalChange("Mood_Mindset", e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select (1=Calm, 5=Stress)</option>
              {MOOD_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        )}

        {/* Grade */}
        {!settings["Hide Grade"] && (
        <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label>
            <select
              id="grade"
              value={journal["Grade"]}
              onChange={e => handleJournalChange("Grade", e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            >
              <option value="">Select</option>
              {GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
        )}
      </div>

      {/* Inputs */}
      <div className="space-y-6">
         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label>
           <input
              id="link"
              type="text"
              value={journal["Links_Screenshots"]}
              onChange={e => handleJournalChange("Links_Screenshots", e.target.value)}
              placeholder="Paste URL here..."
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition"
            />
         </div>
         
         <div>
           <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label>
           <textarea
              id="notes"
              rows={3}
              value={journal["Notes"]}
              onChange={e => handleJournalChange("Notes", e.target.value)}
              placeholder="Anything to note about this trade?"
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y"
            />
         </div>

         {/* NEW: Emotional Notes */}
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
            Emotional Notes
          </label>
          <textarea 
            rows={3} 
            value={journal["Emotional Notes"] || ""} 
            onChange={e => handleJournalChange("Emotional Notes", e.target.value)} 
            placeholder="How were you feeling during this execution? Anxious? Confident? Rushed?"
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-700 dark:text-white bg-gray-50 transition resize-y" 
          />
        </div>

         {/* FLAG FOR REVIEW CHECKBOX */}
         <div className="mt-2">
           <label className="inline-flex items-center text-red-600 dark:text-red-400 font-bold cursor-pointer select-none">
             <input 
               type="checkbox" 
               checked={!!journal["Needs Review"]} 
               onChange={e => handleJournalChange("Needs Review", e.target.checked)} 
               className="mr-2 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500" 
             />
             Flag for Review
           </label>
         </div>

         {!settings["Hide Lesson"] && (
         <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
            <label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">
              Lesson (Optional)
            </label>
            <textarea
              id="lesson"
              rows={2}
              value={journal["Lesson"]}
              onChange={e => handleJournalChange("Lesson", e.target.value)}
              placeholder="What did you learn from this trade?"
              className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-white bg-white transition resize-y"
            />
            <div className="mt-3">
              <label className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={journal["LogLesson"]}
                  onChange={e => handleJournalChange("LogLesson", e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Save to Lessons Database
              </label>
            </div>
         </div>
         )}
      </div>
    </div>
  
  </div>

  {/* Save Button */}
  <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700">
     <button 
       className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-600 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
       onClick={handleSaveTrade} 
       disabled={savingTrade}
     >
        {savingTrade ? "Saving..." : "Save Trade"}
     </button>
  </div>

</div>
)} {/* <--- ADD THIS CLOSING BRACE/PARENTHESIS HERE */}


 {/* --- WIN PROJECTION MODAL --- */}
      {projectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b]
            max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
            
            <button
              className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800 transition"
              onClick={() => setProjectionOpen(false)}
              aria-label="Close"
            >×</button>
              
            <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
                <path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Win Projection Table
            </h3>

            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many trades (using at least your minimum size setting) 
              it would take to reach your goal. Any trades with the red background indicate when you are over your risk tolerance. 
              The totals are estimates and don't take into account broker fees and commissions.
              <br /><br />
              If you prefer to always trade the same contracts, you can set the minimum and maximum contracts to the same number in the settings.
            </p>

            {/* 👇 THE TOGGLE BLOCK 👇 */}
            <div className="mb-4 flex items-center justify-end">
              <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                <span className="mr-3">Group by Trading Day</span>
                <div className="relative flex items-center">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={showDaySeparators} 
                    onChange={() => setShowDaySeparators(!showDaySeparators)} 
                  />
                  <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                  <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>

            {/* --- DYNAMIC TIME ESTIMATION BLOCK --- */}
            {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const trades = winProjection?.trades || [];
              const tradeCount = winProjection?.tradeCount || 0;

              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const isProp = (init > 0 && maxLoss > 0);
              const ruinThreshold = isProp ? Math.max(0, init - maxLoss) : 0;

              if (running <= ruinThreshold) {
                return (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm font-bold text-red-800 dark:text-red-200">
                    Your account balance has reached your maximum loss limit or $0.
                  </div>
                );
              }

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal)}.
                  </div>
                );
              }

              let estimatedDays = 0;
              let currentDayAccumulator = 0;

              if (dailyGoal > 0) {
                for (const t of trades) {
                  const profit = t.tradeProfit || 0;
                  if (profit >= dailyGoal) {
                    estimatedDays++;
                    currentDayAccumulator = 0; 
                  } else {
                    currentDayAccumulator += profit;
                    if (currentDayAccumulator >= dailyGoal) {
                      estimatedDays++;
                      currentDayAccumulator = 0;
                    }
                  }
                }
                if (currentDayAccumulator > 0) estimatedDays++;
              } else {
                estimatedDays = tradeCount;
              }

              return (
                <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
                  <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                    <FaRegClock className="text-blue-500" />
                    Estimated Time to Goal
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                    {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">winning trades in ≈ </span> {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span> 
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                    To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades in about {estimatedDays} if you reached your trading daily goal.
                  </p>
                </div>

                    <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                      <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>While trade profit &lt; <strong>{formatCurrency(dailyGoal)}</strong>, we assume you trade until you hit your daily goal.</li>
                        <li>Once trade profit &ge; <strong>{formatCurrency(dailyGoal)}</strong>, we assume 1 trade per day.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {winProjection?.tradeCount > 0 && (() => {
              const r = Number(settings["Running Balance"] || 0);
              const i = Number(settings["Initial Balance"] || 0);
              const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : 0;
              return r > thresh; 
            })() && (
              <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-blue-200 dark:border-blue-800 shadow">
                <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                  <thead className="bg-blue-100 dark:bg-blue-900/80 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Max Drawdown</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold"># Contracts</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Risk</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Profit</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let runningBal = Number(settings["Running Balance"] || 0); 
                      const rowsToShow = isTrialing ? winProjection.trades.slice(0, 10) : winProjection.trades;

                      let currentDay = 1;
                      let dayAccumulator = 0;
                      const dailyGoalAmt = Number(settings["Daily Goal"] || 0);

                      const tradesWithDays = rowsToShow.map((row) => {
                        const assignedDay = currentDay;
                        if (dailyGoalAmt > 0) {
                          dayAccumulator += (row.tradeProfit || 0);
                          if (dayAccumulator >= dailyGoalAmt) {
                            currentDay++;
                            dayAccumulator = 0;
                          }
                        }
                        return { ...row, day: assignedDay };
                      });

                      return tradesWithDays.map((row, i) => {
                        const before = runningBal;                  
                        const after  = before + (row.tradeProfit ?? 0); 
                        runningBal = after;                         

                        const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDays[i-1].day;

                        return (
                          <React.Fragment key={i}>
                            {isNewDay && (
                              <tr className="bg-blue-100/50 dark:bg-blue-900/30">
                                <td colSpan="7" className="px-3 py-2 text-center text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-widest border-y border-blue-200 dark:border-blue-800">
                                  — Day {row.day} —
                                </td>
                              </tr>
                            )}
                            
                            <tr className={`${row.underfunded ? "bg-red-100 dark:bg-red-900/80" : "hover:bg-blue-50 dark:hover:bg-blue-800/80"} transition border-b border-gray-100 dark:border-gray-800`}>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(before)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.riskDollar)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.contracts}</td>
                              <td className={`px-3 py-2 ${row.contracts === 0 ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>
                                {formatCurrency(row.totalRisk)}
                              </td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.tradeProfit)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(after)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
                {isTrialing && winProjection.trades.length > 5 && (
                  <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-lg font-bold text-gray-600 dark:text-red-500">Full projection table available after Free Trial</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- LOSS PROJECTION MODAL --- */}
      {lossProjectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616]
            max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
            <button
              className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800"
              onClick={() => setLossProjectionOpen(false)}
              aria-label="Close"
            >×</button>

            <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" />
                <path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Loss Projection Table
            </h3>

            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many consecutive full-loss trades (using at least your minimum contract setting) it would take to blow your account. The totals are estimates and do not include fees.
              <br /><br />
              If you prefer to always trade the same number of contracts, you can set the minimum and maximum number of contracts to the same number in the settings.
            </p>

            {/* 👇 THE TOGGLE BLOCK 👇 */}
            <div className="mb-4 flex items-center justify-end">
              <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                <span className="mr-3">Group by Trading Day</span>
                <div className="relative flex items-center">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={showDaySeparators} 
                    onChange={() => setShowDaySeparators(!showDaySeparators)} 
                  />
                  <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                  <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>

            {/* --- DYNAMIC SUMMARY BLOCK --- */}
            {(() => {
              const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
              const init      = Number(settings["Initial Balance"] || 0);
              const maxLoss   = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const running   = Number(settings["Running Balance"] || 0);
              const tradeCount = lossProjection?.tradeCount || 0;

              if (!(dailyLoss > 0)) return null;

              let daysToRuin = 0;
              let ruinThreshold = 0;
              let isProp = (init > 0 && maxLoss > 0);

              if (isProp) {
                ruinThreshold = Math.max(0, init - maxLoss);
                const distance = Math.max(0, running - ruinThreshold);
                daysToRuin = Math.ceil(distance / dailyLoss);
              } else {
                daysToRuin = Math.ceil(running / dailyLoss);
              }

              return (
                <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
                  <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-500" />
                    Risk to Ruin Analysis
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                        {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        To reach your failure threshold of <strong>{formatCurrency(ruinThreshold)}</strong> based on your current risk settings.
                      </p>
                    </div>

                    <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                      <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                        {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                        If you hit your Daily Loss Limit ({formatCurrency(dailyLoss)}) every single day.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-red-200 dark:border-red-800 shadow">
              <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Trade #</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance Before</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold"># Contracts</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Loss Amount</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {(lossProjection?.trades?.length ?? 0) === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                        No trades can be taken with the current settings.
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      const rowsToShow = isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades;
                      
                      let currentLossDay = 1;
                      let lossAccumulator = 0;
                      const dailyLossAmt = Number(settings["Daily Loss Limit"] || 0);

                      const tradesWithDaysLoss = rowsToShow.map(row => {
                        const assignedDay = currentLossDay;
                        if (dailyLossAmt > 0) {
                          lossAccumulator += (row.loss || 0);
                          if (lossAccumulator >= dailyLossAmt) {
                            currentLossDay++;
                            lossAccumulator = 0; 
                          }
                        }
                        return { ...row, day: assignedDay };
                      });

                      return tradesWithDaysLoss.map((row, i) => {
                        const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDaysLoss[i-1].day;

                        return (
                          <React.Fragment key={i}>
                            {isNewDay && (
                              <tr className="bg-red-100/50 dark:bg-red-900/30">
                                <td colSpan="5" className="px-4 py-2 text-center text-xs font-bold text-red-800 dark:text-red-300 uppercase tracking-widest border-y border-red-200 dark:border-red-800">
                                  — Day {row.day} —
                                </td>
                              </tr>
                            )}
                            
                            <tr className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                                {formatCurrency(row.balanceBefore)}
                              </td>
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                                {row.contracts}
                              </td>
                              <td className="px-4 py-2 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">
                                {formatCurrency(row.loss)}
                              </td>
                              <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                                {formatCurrency(row.balanceAfter)}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()
                  )}
                </tbody>
              </table>
              {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500">Full projection table available after Free Trial</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}




    {/* --- POPUPS AND WARNINGS --- */}

{showZeroContractsPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setShowZeroContractsPopup(false)}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        Based on the product you selected, your account balance, risk tolerance, and stop loss size, the suggested number of contracts for this trade is <span className="font-bold text-red-500">0</span>.
      </p>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You are currently risking <span className="font-bold text-red-500">{lastActualRiskPercent}%</span> of your balance, which is <span className="font-bold text-red-500">{formatCurrency(lastTotalRisk)}</span>.
      </p>

      {/* ▼▼ NEW: tip that uses the user's *minimum* contracts instead of hard-coding 1 ▼▼ */}
      {(() => {
        // Use the min contracts from the calculation, falling back to settings
        const minQty = Number(calculatedSummary?.minContracts || settings["Minimum Contracts"] || 1);

        // maxStopLossSize is for 1 contract; divide by minQty to get ticks allowed for minQty contracts
        const maxSLForMin = (Number(calculatedSummary?.maxStopLossSize) > 0 && minQty > 0)
          ? Math.floor(Number(calculatedSummary.maxStopLossSize) / minQty)
          : 0;

        if (maxSLForMin <= 0) return null; // don’t show if not meaningful

        return (
          <p className="mb-6 text-gray-800 dark:text-gray-100">
            Tip: To trade <span className="font-semibold">{minQty}</span> contract{minQty > 1 ? "s" : ""} within your risk, your stop loss must be ≤{" "}
            <span className="font-bold text-yellow-600">{maxSLForMin}</span> ticks.
          </p>
        );
      })()}
      {/* ▲▲ NEW block ends ▲▲ */}

      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider choosing a different product or waiting for a trade setup that requires a smaller stop loss.
      </p>

      <button
        onClick={() => setShowZeroContractsPopup(false)}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}







{currentPopup?.id === 'accountGoalReached' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    {/* Confetti for celebration! */}
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" strokeWidth="2" />
        </svg>
        Account Goal Achieved!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Congratulations! You've reached your account goal!
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Your balance is  <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span><br />
        Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span>
      </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Your hard work has paid off!
        </span>
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'tradeSaved' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
      <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
        <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/>
          <path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </span>
      <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">
        Trade Saved!
      </div>
      <div className="text-gray-700 dark:text-gray-200 mb-1 text-center">
        Your journal entry has been saved.
      </div>
      <button
        className="mt-2 px-5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition"
        onClick={closeCurrentPopup}
      >
        OK
      </button>
    </div>
  </div>
)}


{/* REQUIRED FIELDS */}
{missingFields.length > 0 && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setMissingFields([])}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Required Fields Missing
      </h2>
      <ul className="mb-4 text-gray-800 dark:text-gray-200">
        {missingFields.map((f) => (
   <li key={f} className="pl-2">{f}</li>
 ))}

      </ul>
      <button
        onClick={() => setMissingFields([])}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'twoLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - 2 Consecutive Losses!
      </h2>

      {/* MODIFICATION IS HERE */}
      <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 text-left">
        {TWO_LOSS_WARNING_TEXT.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {highlightHundredPercent(line)}
            <br />
          </React.Fragment>
        ))}
      </div>
      {/* END OF MODIFICATION */}

      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'threeLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="three-loss-title"
      aria-describedby="three-loss-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="three-loss-title"
        className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Danger!
      </h2>

      <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        {currentPopup.data?.message.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {formatThreeLossMessage(line)}
            <br />
          </React.Fragment>
        ))}

        <div className="mt-4 text-blue-400 font-bold">
          {"You got this!!"}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        {currentPopup.data?.videoLink && (
          <a
            href={currentPopup.data.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition"
          >
            {/* Play icon */}
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6 4l10 6-10 6V4z" />
            </svg>
            Watch: Dealing with Trading Losses
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}

        <button
          onClick={closeCurrentPopup}
          className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}




{/* --- ADD THIS FLOATING ACTION BUTTON AND CALCULATOR --- */}
    <button
      onClick={() => setShowCalculator(true)}
      title="Open Calculator"
      className="fixed bottom-12 right-6 z-40 bg-gray-800 dark:bg-gray-700 text-white rounded-full p-4 shadow-lg hover:bg-gray-900 dark:hover:bg-gray-600 transition-transform transform hover:scale-110"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    </button>

{/* Strategy Checklist Floating Panel */}
      {showStrategyModal && (
        <div className="fixed top-24 right-8 z-40 animate-fadeIn pointer-events-auto w-96 max-w-[calc(100vw-2rem)] shadow-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border-2 border-blue-500 relative max-h-[80vh] flex flex-col">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setShowStrategyModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2 shrink-0">
              <FaClipboardList className="text-blue-500" />
              Strategy Checklist
            </h2>
            
            <div className="overflow-y-auto mb-4 pr-2 flex-1">
              {userPrefs?.tradingRules ? (
                <div className="space-y-3">
                  {userPrefs.tradingRules.split('\n').filter(rule => rule.trim() !== '').map((rule, index) => (
                    <label key={index} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition">
                      <input 
                        type="checkbox" 
                        className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" 
                      />
                      <span className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed text-sm">
                        {rule}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="mb-2">No Trading Rules defined yet.</p>
                  <p className="text-sm">Head over to the Lessons page to define your core strategy.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
              <button
                onClick={() => setShowStrategyModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-xl shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    
    {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
    {/* --- END OF NEW CODE --- */}
  </div>
);
}